import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { candidateAPI, clientAPI, requirementAPI, emailAPI, settingsAPI, submissionAPI } from '../utils/api';
import usePersistentState, { readPersisted } from '../utils/usePersistentState';
import { authorOptions } from '../utils/authorOptions';
import { htmlToText } from '../utils/html';
// Note: emailAPI is kept for sendJd functionality
import { useAuth } from '../context/AuthContext';
import { formatActor } from '../utils/attribution';
import RichTextEditor from '../components/RichTextEditor';
import SafeHtml from '../components/SafeHtml';
import { useTheme } from '../context/ThemeContext';
import {
  Button, Input, Select, Modal,
  EmptyState, Pagination, Spinner, SearchableSelect} from '../components/UI';
import HistoryPanel from '../components/HistoryPanel';
import GlobalHistoryPanel from '../components/GlobalHistoryPanel';

// ─── Numeric input filters (keep only valid numeric chars; block wheel-scroll edits) ──
const onlyDigits  = (v) => String(v ?? '').replace(/[^0-9]/g, '');
const onlyDecimal = (v) => String(v ?? '').replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

// ─── Country dial-code list ───────────────────────────────────────────────────
const COUNTRY_DIAL = [
  { code: '+91',  iso: 'in', name: 'India'        },
  { code: '+1',   iso: 'us', name: 'USA'           },
  { code: '+44',  iso: 'gb', name: 'UK'            },
  { code: '+61',  iso: 'au', name: 'Australia'     },
  { code: '+971', iso: 'ae', name: 'UAE'           },
  { code: '+65',  iso: 'sg', name: 'Singapore'     },
  { code: '+49',  iso: 'de', name: 'Germany'       },
  { code: '+33',  iso: 'fr', name: 'France'        },
  { code: '+81',  iso: 'jp', name: 'Japan'         },
  { code: '+86',  iso: 'cn', name: 'China'         },
  { code: '+1',   iso: 'ca', name: 'Canada'        },
  { code: '+55',  iso: 'br', name: 'Brazil'        },
  { code: '+27',  iso: 'za', name: 'South Africa'  },
  { code: '+82',  iso: 'kr', name: 'South Korea'   },
  { code: '+60',  iso: 'my', name: 'Malaysia'      },
];

function FlagIcon({ iso, size = 20 }) {
  return (
    <img
      src={`https://flagcdn.com/w40/${iso}.png`}
      alt={iso}
      width={size}
      height={size * 0.75}
      style={{ borderRadius: 3, objectFit: 'cover', display: 'block', flexShrink: 0 }}
      onError={e => { e.target.style.display = 'none'; }}
    />
  );
}

function CountryCodePicker({ value, iso: selectedIso, onChange, isDark }) {
  const [open, setOpen]       = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 200 });
  const btnRef = useRef(null);

  const selected = COUNTRY_DIAL.find(c => c.iso === selectedIso && c.code === value)
    || COUNTRY_DIAL.find(c => c.code === value)
    || COUNTRY_DIAL[0];

  // Position the fixed dropdown under the trigger button
  const openDrop = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      // Flip upward if too close to bottom of viewport
      const spaceBelow = window.innerHeight - r.bottom;
      const dropH = Math.min(240, COUNTRY_DIAL.length * 36 + 8);
      const top = spaceBelow < dropH + 8 ? r.top - dropH - 4 : r.bottom + 4;
      setDropPos({ top, left: r.left, width: Math.max(200, r.width) });
    }
    setOpen(v => !v);
  };

  // Close on outside click or scroll
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', close, true);
    };
  }, [open]);

  const border     = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0';
  const bg         = isDark ? 'var(--bg-input)' : '#fff';
  const hoverBg    = isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9';
  const textColor  = isDark ? '#f1f5f9' : '#111827';
  const mutedColor = isDark ? '#94a3b8' : '#6b7280';
  const dropBg     = isDark ? '#1e293b' : '#ffffff';
  const dropShadow = isDark ? '0 8px 32px rgba(0,0,0,0.6)' : '0 8px 32px rgba(0,0,0,0.14)';

  return (
    <>
      {/* Trigger */}
      <button
        ref={btnRef}
        type="button"
        onClick={e => { e.stopPropagation(); openDrop(); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: bg, border, borderRadius: 8,
          padding: '8px 10px', cursor: 'pointer',
          minWidth: 90, height: 38, flexShrink: 0,
          color: textColor, fontSize: 13, fontWeight: 500,
          transition: 'border-color 0.15s',
          userSelect: 'none',
        }}
      >
        <FlagIcon iso={selected.iso} />
        <span>{selected.code}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
          style={{ marginLeft: 'auto', opacity: 0.45, transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}>
          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Portal dropdown — renders into document.body to escape modal stacking context */}
      {open && ReactDOM.createPortal(
        <div
          onMouseDown={e => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: dropPos.top,
            left: dropPos.left,
            width: dropPos.width,
            zIndex: 999999,
            background: dropBg,
            border,
            borderRadius: 10,
            boxShadow: dropShadow,
            maxHeight: 240,
            overflowY: 'auto',
            padding: 4,
          }}
        >
          {COUNTRY_DIAL.map((c, i) => {
            const isActive = c.code === value && c.iso === selected.iso;
            return (
              <button
                key={`${c.iso}-${i}`}
                type="button"
                onMouseDown={e => e.stopPropagation()}
                onClick={() => { onChange(c.code, c.iso); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '7px 10px', borderRadius: 7,
                  background: isActive ? (isDark ? 'rgba(99,102,241,0.18)' : '#eef2ff') : 'transparent',
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = hoverBg; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <FlagIcon iso={c.iso} size={18} />
                <span style={{ fontSize: 12, color: textColor, flex: 1 }}>{c.name}</span>
                <span style={{ fontSize: 12, color: mutedColor, fontWeight: 600, flexShrink: 0 }}>{c.code}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

const CURRENCIES    = ['USD', 'INR', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD', 'JPY'];
const BILLING_TYPES = ['Fixed', 'Hourly', 'Monthly', 'Percentage'];

// NOTE: the numeric relevantExperience and numeric noticePeriod / last-working
// -date inputs were retired from data entry — HR captures those details once,
// in the rich-text "Relevant Experience Summary" and "Notice Period Notes"
// fields instead (the Fixed Days / Serving toggle stays — it's a one-click
// flag, not duplicate typing). The retired keys are deliberately ABSENT from
// form state so edit-saves never echo (or wipe) the legacy values still stored
// on old candidates; those stay visible in read-only views.
const emptyForm = {
  fullName: '', email: '', phone: '', phoneCountryCode: '+91', phoneIso: 'in', skills: '', experience: '',
  experienceYrs: '', experienceMos: '',
  currentCompany: '', currentRole: '', linkedInUrl: '', location: '', preferredLocation: '',
  expectedSalary: '', expectedSalaryCurrency: 'INR',
  currentSalary:  '', currentSalaryCurrency:  'INR',
  servingNoticePeriod: false, offerInHand: false,
  availabilityToInterview: '',
  experienceSummary: '',
  noticePeriodNotes: '',
  offerCompany: '', offerPackage: '', offerDoj: '',
  // First-submission fields (create mode only)
  clientId: '', requirementId: '',
  overrideBillingCost: '', overrideBillingCurrency: 'USD', overrideBillingType: '',
  notes: '', clientNotes: '',
};

// Convert years float → { yrs, mos } display values
const splitExp = (val) => {
  const n = parseFloat(val);
  if (isNaN(n) || n < 0) return { yrs: '', mos: '' };
  const yrs = Math.floor(n);
  const mos = Math.round((n - yrs) * 12);
  return { yrs: yrs === 0 && mos > 0 ? '' : String(yrs), mos: mos > 0 ? String(mos) : '' };
};

// Convert { yrs, mos } → single float for the DB
const combineExp = (yrs, mos) => {
  const y = parseFloat(yrs) || 0;
  const m = parseFloat(mos) || 0;
  const total = y + m / 12;
  return total > 0 ? parseFloat(total.toFixed(4)) : '';
};

// ─── PDF Resume Uploader ──────────────────────────────────────────────────────
function ResumeUploader({ candidateId, resumeName, resumeSize, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading]   = useState(false);
  const [dragOver, setDragOver]     = useState(false);
  const [loadingUrl, setLoadingUrl] = useState(false);

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024)    return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(2)} MB`;
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Only PDF files are accepted.'); return; }
    if (file.size > 5 * 1024 * 1024)    { toast.error('File must be under 5 MB.');      return; }

    setUploading(true);
    const fd = new FormData();
    fd.append('resume', file);
    try {
      const { data } = await candidateAPI.uploadResume(candidateId, fd);
      toast.success('Resume uploaded to S3!');
      onUploaded(data.resumeName, data.resumeSize);
    } catch (err) {
      toast.error(err.response?.data?.message
        || (err.response ? 'Upload failed.' : 'No response from the server — the resume may not be saved. Please try again.'));
    } finally {
      setUploading(false);
    }
  };

  const openResume = async (e, download = false) => {
    e.stopPropagation();
    setLoadingUrl(true);
    try {
      const { data } = await candidateAPI.getResumeUrl(candidateId);
      // S3 path — API returns { url: '...' }
      if (data.url) {
        if (download) {
          const a = document.createElement('a');
          a.href = data.url;
          a.download = data.resumeName || 'resume.pdf';
          a.click();
        } else {
          window.open(data.url, '_blank');
        }
      }
    } catch (err) {
      // No silent fallback to /candidates/:id/resume any more. That URL cannot
      // carry the JWT and relied on server-held state, so behind two pods it
      // failed about half the time with "This link has expired" — a worse
      // outcome than an honest error, because it looked like the CV was gone.
      const status = err?.response?.status;
      toast.error(status === 404
        ? 'No resume on file for this candidate.'
        : 'Could not open the resume. Please try again.');
    } finally {
      setLoadingUrl(false);
    }
  };

  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>
        Resume — PDF stored in AWS S3 (max 5 MB)
      </label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => fileRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? '#6366f1' : 'var(--border-input)'}`,
          borderRadius: 10, padding: 20, textAlign: 'center', cursor: 'pointer',
          background: dragOver ? 'rgba(99,102,241,0.06)' : 'transparent',
          transition: 'all 0.15s ease',
        }}
      >
        <input ref={fileRef} type="file" accept="application/pdf"
          style={{ display: 'none' }} onChange={(e) => { handleFile(e.target.files[0]); e.target.value = ''; }} />

        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Spinner size="sm" />
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Uploading to S3…</p>
          </div>
        ) : resumeName ? (
          <div className="flex flex-col items-center gap-1">
            <span className="text-3xl">📄</span>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }} className="truncate max-w-xs">{resumeName}</p>
            <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatSize(resumeSize)} · stored in S3</p>
            <div className="flex gap-4 mt-2" onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="xs" onClick={(e) => openResume(e, false)} disabled={loadingUrl}>View PDF ↗</Button>
              <Button variant="ghost" size="xs" onClick={(e) => openResume(e, true)} disabled={loadingUrl}>Download</Button>
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Click or drag to replace</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <span className="text-3xl">📤</span>
            <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Drop PDF here or click to browse</p>
            <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>PDF only · max 5 MB · saved to PostgreSQL</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Candidates page ─────────────────────────────────────────────────────
export default function Candidates() {
  const { isSuperAdmin, hasRole, isDeveloper, user, showAttribution } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate  = useNavigate();
  const canEdit     = hasRole(['SuperAdmin', 'RecruiterManager']);
  const isRecruiter = hasRole(['Recruiter']);
  const canDelete = isSuperAdmin();
  const canCreate = hasRole(['SuperAdmin', 'RecruiterManager', 'Recruiter', 'Developer']);
  const canEmail  = hasRole(['SuperAdmin', 'RecruiterManager']);

  const [candidates, setCandidates]       = useState([]);
  const [total, setTotal]                 = useState(0);
  const [pages, setPages]                 = useState(1);
  const [page, setPage]                   = useState(1);
  const [search, setSearch]               = useState(() => readPersisted('candidates:search', ''));
  // requirementStatus cascades: it narrows the requirement dropdown options
  // AND filters the candidate list to those whose submissions are on a
  // requirement matching the chosen status. When a specific requirement is
  // picked, requirementId wins on the backend (see candidateController).
  // Search and filters are kept for this tab (survive leaving the page).
  const [filters, setFilters]             = usePersistentState('candidates:filters', { clientId: '', requirementId: '', requirementStatus: '', minExp: '', maxExp: '', recruiterId: '' });
  // "Recruiter" filter — who added the candidate. SuperAdmin and
  // RecruiterManager only.
  const canFilterByRecruiter = hasRole(['SuperAdmin', 'RecruiterManager']);
  const [authors, setAuthors]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [modalOpen, setModalOpen]         = useState(false);
  const [editCandidate, setEditCandidate] = useState(null);
  const [form, setForm]                   = useState(emptyForm);
  const [saving, setSaving]               = useState(false);
  const [loadingResume, setLoadingResume] = useState(null); // candidate id being fetched
  const [loadingEdit, setLoadingEdit]     = useState(null); // candidate id being opened
  const [sendJdEmail, setSendJdEmail]     = useState(false);
  const [clients, setClients]             = useState([]);
  // ← Per-entity history panel
  const [historyPanel, setHistoryPanel]   = useState({ open: false, id: null, name: '' });
  // ← Global candidate history feed
  const [globalHistory, setGlobalHistory] = useState(false);

  const [allRequirements, setAllRequirements] = useState([]);
  const [loadingReqs, setLoadingReqs]         = useState(false);

  // ── Resubmit modal ───────────────────────────────────────────────────────
  const [resubmitCandidate,       setResubmitCandidate]       = useState(null);
  const [resubmitEditForm,        setResubmitEditForm]        = useState({});
  const [resubmitReqId,           setResubmitReqId]           = useState('');
  const [resubmitNotes,           setResubmitNotes]           = useState('');
  const [resubmitClientNotes,     setResubmitClientNotes]     = useState('');
  const [resubmitBillingCost,     setResubmitBillingCost]     = useState('');
  const [resubmitBillingCurrency, setResubmitBillingCurrency] = useState('USD');
  const [resubmitBillingType,     setResubmitBillingType]     = useState('');
  const [resubmitting,            setResubmitting]            = useState(false);

  // Track resume metadata — name+size only (bytes live in PostgreSQL)
  const [pendingResume, setPendingResume] = useState({ name: null, size: null });

  // ── CV auto-fill (create flow) ────────────────────────────────────────────
  // cvFile: the File object stored until after candidate creation for upload.
  // The resume is MANDATORY on create — the form only appears once it's set.
  const [cvFile, setCvFile]           = useState(null);
  const [parsing, setParsing]         = useState(false);
  const [cvExtracted, setCvExtracted] = useState(false);
  const [cvDragOver, setCvDragOver]   = useState(false);
  // ─────────────────────────────────────────────────────────────────────────

  // ── Checklist state ───────────────────────────────────────────────────────
  // checklistItems: configured items [{ id, label }] loaded from API
  // checkedItems:   Set of item ids the user has ticked in the current modal
  const [checklistItems, setChecklistItems] = useState([]);
  const [checkedItems, setCheckedItems]     = useState(new Set());
  // ─────────────────────────────────────────────────────────────────────────

  // ── Duplicate detection (email + phone) ──────────────────────────────────
  // duplicateWarning: null | { candidate: {...}, matchedBy: 'email'|'phone' }
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const dupCheckTimer = useRef(null);
  // Latest field values for the debounce — kept in a ref so the timeout closure
  // always reads the most recent values without needing to re-register.
  const dupFieldsRef = useRef({ email: '', phone: '' });
  // ─────────────────────────────────────────────────────────────────────────

  // Search box value vs. the debounced term the list is loaded with: typing
  // fires one request ~300ms after the last keystroke instead of one per key.
  const [searchInput, setSearchInput]     = usePersistentState('candidates:search', '');
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Only the latest list request may update the screen. Requests land on
  // different pods with different latency, so an earlier query ('jo') can
  // resolve after a later one ('john') and would otherwise overwrite it.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const { data } = await candidateAPI.getAll({ page, limit: 6, search, ...filters });
      if (seq !== loadSeq.current) return;
      setCandidates(data.candidates);
      setTotal(data.total);
      setPages(data.pages);
      // Filter options ride along with the list — see utils/authorOptions.
      setAuthors(data.authors || []);
    } catch {
      if (seq === loadSeq.current) toast.error('Failed to load candidates');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [page, search, filters]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setLoadingReqs(true);
    requirementAPI.dropdown()
      .then(({ data }) => setAllRequirements(data.requirements || []))
      .catch(() => setAllRequirements([]))
      .finally(() => setLoadingReqs(false));
  }, []);

  // Load clients list for SuperAdmin filter dropdown
  useEffect(() => {
    if (!isSuperAdmin()) return;
    clientAPI.getAll({ limit: 500 })
      .then(({ data }) => setClients(data.clients || []))
      .catch(() => setClients([]));
  }, []);

  // JD email is mandatory for a brand-new candidate whenever the chosen
  // requirement has a job description on file — force the checkbox on so it
  // can't be silently skipped. When a requirement IS selected but has NO JD,
  // force it OFF: the JD email is blocked (nothing to send). (No-op on edit.)
  useEffect(() => {
    if (editCandidate) return;
    if (!form.requirementId) return;
    const req = allRequirements.find(r => r.id === form.requirementId);
    setSendJdEmail(!!req?.hasJobDescription);
  }, [editCandidate, form.requirementId, allRequirements]);

  const handleRequirementChange = (rid) => {
    const req = allRequirements.find(r => r.id === rid);
    setForm(prev => ({
      ...prev,
      requirementId: rid,
      clientId: req?.clientId || prev.clientId,
    }));
    // The per-requirement checklist changes with the requirement — re-confirm all.
    setCheckedItems(new Set());
  };

  const openCreate = async () => {
    setEditCandidate(null);
    setForm(emptyForm);
    setPendingResume({ name: null, size: null });
    setSendJdEmail(true);   // checked by default on new candidate
    setDuplicateWarning(null);
    dupFieldsRef.current = { email: '', phone: '' };
    // Reset CV upload state
    setCvFile(null);
    setCvExtracted(false);
    setParsing(false);
    // Load checklist items and reset checked state
    try {
      const { data } = await settingsAPI.getChecklist();
      setChecklistItems(data.items || []);
    } catch { setChecklistItems([]); }
    setCheckedItems(new Set());
    setModalOpen(true);
  };

  // ── PDF.js loader (CDN, loaded once) ─────────────────────────────────────
  const loadPdfJs = () => new Promise((resolve, reject) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('Could not load PDF.js'));
    document.head.appendChild(script);
  });

  // ── Extract fields from raw text using regex ──────────────────────────────
  const extractFieldsFromText = (text) => {
    // ── Helpers ────────────────────────────────────────────────────────────────
    // Try a list of regex patterns; return first capture group that matches
    const first = (patterns, src = text) => {
      for (const p of patterns) {
        const m = src.match(p);
        if (m) return (m[1] || m[0]).trim().replace(/[.\s]+$/, '');
      }
      return '';
    };

    // Extract a labeled section: find heading alias, grab lines until next section.
    // Handles blank-line separators between sections (\n+ instead of \n).
    const NEXT_SECTION_WORDS = [
      'work experience','professional experience','employment history','employment','career summary','career',
      'education','academic background','academic','qualification',
      'projects','project details','key projects',
      'certifications?','certificates?',
      'achievements?','awards?','honors?',
      'publications?',
      'personal details?','personal information','personal profile',
      'additional details?','additional information',
      'languages?','hobbies','interests',
      'references?','declaration',
      'professional summary','executive summary','summary','objective','profile','about me',
    ];
    const NEXT_SEC_PAT = NEXT_SECTION_WORDS.join('|');

    const extractSection = (headingAliases) => {
      // Do NOT escape the aliases — they may contain regex quantifiers like ? and +
      const headPat = headingAliases.join('|');
      // Use \n+ to handle blank lines between sections
      const re = new RegExp(
        `(?:^|\\n)(?:${headPat})\\s*[:\\n]((?:.|\\n){5,1200}?)(?=\\n+(?:${NEXT_SEC_PAT})\\s*[:\\n]|$)`,
        'i'
      );
      const m = text.match(re);
      return m ? m[1] : '';
    };

    // ── Email ──────────────────────────────────────────────────────────────────
    const emailMatch = text.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/);
    const email = emailMatch?.[0]?.toLowerCase() || '';

    // ── Phone ──────────────────────────────────────────────────────────────────
    const phoneMatch =
      text.match(/(?:\+91[-.\s]?)?[6-9]\d{9}/) ||
      text.match(/\+?1[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/) ||
      text.match(/\+?[\d][\d\s\-().]{8,14}\d/);
    let phone = '';
    let phoneCountryCode = '+91';
    let phoneIso = 'in';
    if (phoneMatch) {
      const raw = phoneMatch[0];
      if (raw.startsWith('+1') || raw.startsWith('1-')) { phoneCountryCode = '+1'; phoneIso = 'us'; }
      else if (raw.startsWith('+44')) { phoneCountryCode = '+44'; phoneIso = 'gb'; }
      else if (raw.startsWith('+61')) { phoneCountryCode = '+61'; phoneIso = 'au'; }
      else if (raw.startsWith('+971')) { phoneCountryCode = '+971'; phoneIso = 'ae'; }
      else if (raw.startsWith('+65')) { phoneCountryCode = '+65'; phoneIso = 'sg'; }
      const digits = raw.replace(/[^\d]/g, '');
      phone = digits.startsWith('91') && digits.length === 12 ? digits.slice(2) : digits.slice(-10);
    }

    // ── Full Name ──────────────────────────────────────────────────────────────
    // Scan first 12 lines: prefer 2–4 all-capitalized proper words; handle split lines
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    const isProperWord = (w) => /^[A-Z][a-z]{1,20}$/.test(w);
    const isNameLine = (l) =>
      l.length >= 3 && l.length <= 60 &&
      !l.includes('@') && !l.includes('http') && !l.includes('|') && !l.includes(':') &&
      !/\d{3,}/.test(l) && /^[A-Za-z][A-Za-z\s.'-]{2,}$/.test(l) &&
      l.split(/\s+/).length >= 2 && l.split(/\s+/).length <= 5;

    let fullName = '';
    for (let i = 0; i < Math.min(lines.length, 12); i++) {
      const l = lines[i];
      if (isNameLine(l)) {
        const words = l.split(/\s+/);
        if (words.every(isProperWord)) { fullName = l; break; }
        if (!fullName) fullName = l;
      }
      // Two consecutive single-word capitalized lines → "First Last"
      if (!fullName || fullName.split(/\s+/).length > 2) {
        const nxt = lines[i + 1] || '';
        if (isProperWord(l) && isProperWord((nxt.split(/\s+/)[0] || ''))) {
          const nxtWords = nxt.split(/\s+/);
          if (nxtWords.length <= 2 && nxtWords.every(isProperWord)) {
            fullName = `${l} ${nxt}`.trim(); break;
          }
        }
      }
    }

    // ── Years of Experience ────────────────────────────────────────────────────
    // Aliases: "7 years experience", "7+ years of exp", "total experience: 7 years"
    const expMatch =
      text.match(/\btotal\s+(?:work\s+)?exp(?:erience)?\s*[:\-]\s*(\d+(?:\.\d+)?)\+?\s*(?:years?|yrs?)/i) ||
      text.match(/(\d+(?:\.\d+)?)\+?\s*(?:years?|yrs?)[\s\w]{0,25}(?:experience|exp\b)/i) ||
      text.match(/experience\s+of\s+(\d+(?:\.\d+)?)\+?\s*(?:years?|yrs?)/i);
    const experience = expMatch ? parseFloat(expMatch[1] || expMatch[0].match(/\d+(?:\.\d+)?/)?.[0]) : null;

    // ── Location ───────────────────────────────────────────────────────────────
    // Aliases: Location, Current Location, City, Base Location, Residing in, Based at
    const INDIAN_CITIES = 'bangalore|bengaluru|mumbai|delhi|new delhi|hyderabad|pune|chennai|kolkata|noida|gurgaon|gurugram|ahmedabad|surat|jaipur|lucknow|kochi|coimbatore|remote|work from home';
    const location = first([
      /\b(?:current\s+)?location\s*[:\-]\s*([^\n,|•✓]{3,50})/i,
      /\b(?:city|base\s+location|based\s+(?:in|at)|residing\s+in|lives?\s+in)\s*[:\-]\s*([^\n,|•✓]{3,50})/i,
      new RegExp(`\\b(${INDIAN_CITIES})\\b`, 'i'),
    ]);

    // ── Preferred Location ─────────────────────────────────────────────────────
    const preferredLocation = first([
      /\bpreferred\s+(?:location|city|work\s+(?:location|city))\s*[:\-]\s*([^\n|•✓]{3,60})/i,
      /\blooking\s+(?:for|at)\s+(?:opportunities?\s+in|jobs?\s+in)\s*[:\-]?\s*([^\n|•✓]{3,60})/i,
      /\bopen\s+to\s+(?:relocation\s+to\s+)?([A-Za-z\s,/]{3,40})/i,
    ]);

    // ── Current Company ────────────────────────────────────────────────────────
    // Aliases: Current Company, Present Employer, Current Organization, Currently Working at, etc.
    const currentCompany = first([
      /\bcurrent\s+(?:company|employer|organization|org)\s*[:\-]\s*([^\n|•✓]{3,70})/i,
      /\bpresent\s+(?:company|employer|organization|org)\s*[:\-]\s*([^\n|•✓]{3,70})/i,
      /\bemployer\s*[:\-]\s*([^\n|•✓]{3,70})/i,
      /\bcurrently\s+(?:working\s+(?:at|with|in)|employed\s+(?:at|by))\s+([A-Z][A-Za-z\s&.,()]{2,60}?)(?:\s+as\s+|\s+since\s+|[.\n|])/i,
      /\bworking\s+(?:at|with)\s+([A-Z][A-Za-z\s&.,()]{2,60}?)(?:\s+as\s+|\s+since\s+|[.\n|])/i,
    ]);

    // ── Current Role ───────────────────────────────────────────────────────────
    // Aliases: Current Role, Designation, Present Role, Job Title, Position, Applying as
    const currentRole = first([
      /\bcurrent\s+(?:role|designation|position|title|job\s+title)\s*[:\-]\s*([^\n|•✓]{3,80})/i,
      /\bpresent\s+(?:role|designation|position|title)\s*[:\-]\s*([^\n|•✓]{3,80})/i,
      /\bdesignation\s*[:\-]\s*([^\n|•✓]{3,80})/i,
      /\bposition\s*[:\-]\s*([^\n|•✓]{3,80})/i,
      /\bjob\s+title\s*[:\-]\s*([^\n|•✓]{3,80})/i,
    ]);

    // ── Offer In Hand ──────────────────────────────────────────────────────────
    let offerInHand = false;
    const offerRaw = first([
      /\boffer\s+in\s+hand\s*[:\-]\s*([^\n|•✓]{1,30})/i,
      /\bactive\s+offer\s*[:\-]\s*([^\n|•✓]{1,30})/i,
      /\bholding\s+(?:an?\s+)?offer\s*[:\-]?\s*([^\n|•✓]{1,30})/i,
    ]);
    if (offerRaw) offerInHand = /yes|true|have|1\b/i.test(offerRaw);

    // ── Expected Salary ────────────────────────────────────────────────────────
    // Aliases: Expected Salary, Expected CTC, Expected Package, Salary Expectation, CTC Expected
    let expectedSalary = '';
    let expectedSalaryCurrency = '';
    const salaryMatch = text.match(
      /\b(?:expected\s+(?:salary|ctc|package|compensation)|salary\s+expectation|ctc\s+expected|desired\s+(?:salary|ctc|package))\s*[:\-]\s*(?:(INR|USD|EUR|GBP|AUD|SGD|Rs\.?)\s*)?([\$₹€£¥]?\s*[\d,]+(?:\.\d+)?(?:\s*(?:LPA|lpa|L|lakh|lacs?|k|K|per\s+annum|pa|p\.a\.|\/yr|\/year))?)/i
    );
    if (salaryMatch) {
      const code = (salaryMatch[1] || '').toUpperCase().replace('.', '');
      const numRaw = (salaryMatch[2] || '').trim();
      if (code === 'INR' || code === 'RS' || /₹/.test(numRaw)) expectedSalaryCurrency = 'INR';
      else if (code === 'USD' || /\$/.test(numRaw)) expectedSalaryCurrency = 'USD';
      else if (code === 'GBP' || /£/.test(numRaw)) expectedSalaryCurrency = 'GBP';
      else if (code === 'EUR' || /€/.test(numRaw)) expectedSalaryCurrency = 'EUR';
      else if (code === 'AUD') expectedSalaryCurrency = 'AUD';
      else if (code === 'SGD') expectedSalaryCurrency = 'SGD';
      expectedSalary = numRaw.replace(/[^\d.]/g, '');
    }

    // ── Skills / Core Competencies ─────────────────────────────────────────────
    // COMPREHENSIVE heading aliases — every common resume variation
    const SKILL_HEADINGS = [
      'technical skills?',
      'core competencies',
      'key competencies',
      'competencies',
      'skills? summary',
      'skills? & expertise',
      'skills? and expertise',
      'key skills?',
      'professional skills?',
      'it skills?',
      'software skills?',
      'tools? & technologies',
      'tools? and technologies',
      'technologies',
      'tech stack',
      'technology stack',
      'areas? of expertise',
      'expertise',
      'skills?',                    // plain "Skills"
      'strengths?',
      'proficiencies',
    ];

    let skills = [];
    const skillsBlock = extractSection(SKILL_HEADINGS);
    if (skillsBlock) {
      // Strip sub-section category labels like "Languages:", "Databases:", "Cloud & DevOps:"
      const cleaned = skillsBlock.replace(/^[A-Za-z][A-Za-z\s/&(),-]{2,40}:\s*/gm, '');
      skills = cleaned
        // Split on separators; use \s+/\s+ (not bare /) to preserve "CI/CD", "HTML/CSS"
        .split(/[,|•·\n▪◦–■\t]|\s+\/\s+/)
        .map(s => s.replace(/\s+/g, ' ').trim().replace(/^[-•·▪◦■✓\s]+/, ''))
        .filter(s => {
          if (s.length < 2 || s.length > 45) return false;
          // Skip stopwords
          if (/^(and|or|the|with|for|in|at|of|to|a|an|\d+)$/i.test(s)) return false;
          // Skip anything with a colon at end (leftover sub-heading)
          if (/:$/.test(s)) return false;
          // Skip date patterns like "June 2021", "2021 – Present"
          if (/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\w*\s+\d{4}/i.test(s)) return false;
          if (/\d{4}\s*[-–]\s*(?:\d{4}|present)/i.test(s)) return false;
          // Skip job-title–like phrases that contain em-dash/separator + company names
          if (/—/.test(s)) return false;
          // Skip lines that are clearly sentences (contain common sentence words)
          if (/\b(designed|built|managed|led|developed|architected|implemented|created|worked|collaborated)\b/i.test(s)) return false;
          return true;
        })
        .filter((s, i, arr) => arr.indexOf(s) === i)  // deduplicate
        .slice(0, 25);
    }

    return {
      fullName, email, phone, phoneCountryCode, phoneIso,
      location, preferredLocation,
      experience, skills, currentCompany, currentRole,
      offerInHand,
      expectedSalary, expectedSalaryCurrency,
      linkedInUrl: (() => {
        const m = text.match(/(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/([A-Za-z0-9\-_%]+)\/?/i);
        return m ? `https://www.linkedin.com/in/${m[1]}` : '';
      })(),
    };
  };

  // ── CV parse handler — uses browser-side PDF.js, no backend call needed ───
  const handleCvFile = async (file) => {
    if (!file || file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('The resume must be 5 MB or smaller.');
      return;
    }
    setCvFile(file);
    setPendingResume({ name: file.name, size: file.size });
    setParsing(true);

    try {
      const pdfjsLib = await loadPdfJs();
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      let fullText = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        // Group text items into lines by y-position, then join lines with \n
        // IMPORTANT: do NOT use lineTokens.join(' ') — that embeds '\n' inside spaces,
        // breaking every regex that looks for \n at line boundaries.
        let lastY = null;
        const pageLines = [];
        let curLine = [];
        for (const item of content.items) {
          if (!item.str) continue;
          const y = item.transform?.[5] ?? 0;
          if (lastY !== null && Math.abs(y - lastY) > 2) {
            // New line detected — flush current line
            const line = curLine.join('').trim();
            if (line) pageLines.push(line);
            curLine = [];
          }
          curLine.push(item.str);
          lastY = y;
        }
        if (curLine.length) {
          const line = curLine.join('').trim();
          if (line) pageLines.push(line);
        }
        fullText += pageLines.join('\n') + '\n';
      }
      // Normalize: collapse 3+ consecutive newlines to 2, trim leading/trailing spaces per line
      fullText = fullText.replace(/\n{3,}/g, '\n\n').replace(/[ \t]+\n/g, '\n').replace(/\n[ \t]+/g, '\n');
      console.log('[CV parse] extracted text (first 600 chars):\n', fullText.slice(0, 600));

      const ext = extractFieldsFromText(fullText);
      console.log('[CV parse] extracted fields:', JSON.stringify({ ...ext, skills: ext.skills?.slice(0,5) }, null, 2));
      const hasAnyData = ext.fullName || ext.email || ext.phone || ext.skills?.length;

      if (hasAnyData) {
        setForm(prev => ({
          ...prev,
          fullName:              ext.fullName              || prev.fullName,
          email:                 ext.email                 || prev.email,
          phone:                 ext.phone                 || prev.phone,
          phoneCountryCode:      ext.phoneCountryCode      || prev.phoneCountryCode,
          phoneIso:              ext.phoneIso              || prev.phoneIso,
          location:              ext.location              || prev.location,
          preferredLocation:     ext.preferredLocation     || prev.preferredLocation,
          experience:            ext.experience != null ? String(ext.experience) : prev.experience,
          experienceYrs:         ext.experience != null ? splitExp(ext.experience).yrs : prev.experienceYrs,
          experienceMos:         ext.experience != null ? splitExp(ext.experience).mos : prev.experienceMos,
          skills:                ext.skills?.length ? ext.skills : prev.skills,
          currentCompany:        ext.currentCompany        || prev.currentCompany,
          currentRole:           ext.currentRole           || prev.currentRole,
          offerInHand:           ext.offerInHand           || prev.offerInHand,
          expectedSalary:        ext.expectedSalary        || prev.expectedSalary,
          expectedSalaryCurrency: ext.expectedSalaryCurrency || prev.expectedSalaryCurrency,
          linkedInUrl:           ext.linkedInUrl            || prev.linkedInUrl,
        }));
        setCvExtracted(true);
        if (ext.email) { dupFieldsRef.current.email = ext.email; scheduleDupCheck(); }
        if (ext.phone) { dupFieldsRef.current.phone = ext.phone; scheduleDupCheck(); }
        // Count filled fields for toast
        const filled = [ext.fullName, ext.email, ext.phone, ext.location, ext.currentCompany,
          ext.currentRole, ext.expectedSalary, ext.preferredLocation,
          ext.skills?.length ? 'skills' : ''].filter(Boolean).length;
        toast.success(`${filled} fields extracted from CV! Review and complete the form.`);
      } else {
        // PDF parsed but no structured data found (e.g. scanned image PDF)
        setCvExtracted(true);  // still mark as uploaded so resume gets saved
        toast('CV uploaded. Text could not be extracted — please fill the form manually.', { icon: '📄' });
      }
    } catch (err) {
      console.error('[CV parse]', err);
      // File still stored for upload — just tell user to fill manually
      setCvExtracted(true);
      toast('CV uploaded. Could not read text — fill the form manually.', { icon: '📄' });
    } finally {
      setParsing(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ── Debounced duplicate check (email + phone, create AND edit) ──────────
  const scheduleDupCheck = () => {
    clearTimeout(dupCheckTimer.current);
    dupCheckTimer.current = setTimeout(async () => {
      const { email, phone } = dupFieldsRef.current;
      const countryCode = form.phoneCountryCode || '+91';
      const params = {};
      if (email && email.includes('@')) params.email = email.trim();
      if (phone && phone.replace(/\D/g, '').length >= 7) {
        params.phone = `${countryCode}${phone.trim()}`;
      }
      if (!Object.keys(params).length) return;
      // When editing, pass excludeId so the check doesn't flag the candidate's own values
      if (editCandidate) params.excludeId = editCandidate.id;
      try {
        const { data } = await candidateAPI.checkDuplicate(params);
        if (data.exists) setDuplicateWarning({ candidate: data.candidate, matchedBy: data.matchedBy });
      } catch { /* silent */ }
    }, 600);
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setForm(prev => ({ ...prev, email }));
    setDuplicateWarning(null);
    dupFieldsRef.current.email = email;
    scheduleDupCheck();
  };

  const handlePhoneChange = (e) => {
    // Allow only digits, max 10
    const phone = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm(prev => ({ ...prev, phone }));
    setDuplicateWarning(null);
    dupFieldsRef.current.phone = phone;
    scheduleDupCheck();
  };
  // ─────────────────────────────────────────────────────────────────────────

  const openEdit = async (c) => {
    setLoadingEdit(c.id);
    setEditCandidate(c);
    // Load current checklist config for display (read-only if snapshot exists)
    try {
      const { data } = await settingsAPI.getChecklist();
      setChecklistItems(data.items || []);
    } catch { setChecklistItems([]); }
    // If the candidate has a snapshot, show it as fully checked (read-only)
    if (c.checklistSnapshot && Array.isArray(c.checklistSnapshot)) {
      setCheckedItems(new Set(c.checklistSnapshot.map(i => i.id)));
    } else {
      setCheckedItems(new Set());
    }
    // Split stored phone (e.g. "+919876543210") back into country code + digits
    let splitCode = '+91', splitIso = 'in', splitNum = c.phone || '';
    if (c.phone) {
      // Sort by length desc so "+971" is tried before "+1"
      const sorted = [...COUNTRY_DIAL].sort((a, b) => b.code.length - a.code.length);
      const matched = sorted.find(x => c.phone.startsWith(x.code));
      if (matched) { splitCode = matched.code; splitIso = matched.iso; splitNum = c.phone.slice(matched.code.length); }
    }
    setForm({
      fullName:           c.fullName,
      email:              c.email,
      phone:              splitNum,
      phoneCountryCode:   splitCode,
      phoneIso:           splitIso,
      skills:             Array.isArray(c.skills) ? c.skills.join(', ') : (c.skills || ''),
      experience:         c.experience,
      experienceYrs:      splitExp(c.experience).yrs,
      experienceMos:      splitExp(c.experience).mos,
      currentCompany:     c.currentCompany || '',
      currentRole:        c.currentRole || '',
      linkedInUrl:        c.linkedInUrl || '',
      location:           c.location || '',
      preferredLocation:  c.preferredLocation || '',
      expectedSalary:          c.expectedSalary || '',
      expectedSalaryCurrency:  c.expectedSalaryCurrency || 'INR',
      currentSalary:           c.currentSalary  || '',
      currentSalaryCurrency:   c.currentSalaryCurrency  || 'INR',
      servingNoticePeriod:  c.servingNoticePeriod || false,
      availabilityToInterview: c.availabilityToInterview || '',
      offerInHand:          c.offerInHand || false,
      offerCompany:       c.offerCompany || '',
      offerPackage:       c.offerPackage || '',
      offerDoj:           c.offerDoj ? c.offerDoj.slice(0, 10) : '',
      experienceSummary:      c.experienceSummary || '',
      noticePeriodNotes:      c.noticePeriodNotes || '',
      clientId:               '',
      requirementId:          '',
      overrideBillingCost:    '',
      overrideBillingCurrency:'USD',
      overrideBillingType:    '',
      notes:                  c.notes || '',
      clientNotes:            '',
    });
    // Load existing resume metadata from the candidate object
    setPendingResume({ name: c.resumeName || null, size: c.resumeSize || null });
    setSendJdEmail(false);  // unchecked by default on edit
    setLoadingEdit(null);
    setModalOpen(true);
  };

  // Effective checklist = global items + the SELECTED requirement's own items.
  const reqChecklist = (() => {
    const r = allRequirements.find(x => x.id === form.requirementId);
    return Array.isArray(r?.checklistItems) ? r.checklistItems : [];
  })();
  const effectiveChecklist = [...checklistItems, ...reqChecklist];

  // Selected requirement carries a job description → sending the JD email to the
  // candidate is required when creating them (mandatory checkbox, not optional).
  // No JD on the selected requirement → the JD email is BLOCKED (nothing to send).
  const selectedReqHasJd = !!allRequirements.find(x => x.id === form.requirementId)?.hasJobDescription;
  const jdEmailRequired  = !editCandidate && selectedReqHasJd;
  const jdEmailBlocked   = !editCandidate && !!form.requirementId && !selectedReqHasJd;

  const handleSave = async (e) => {
    e.preventDefault();
    // ── Resume is mandatory on create (the form is gated behind it too —
    //    this guard is the backstop in case state is ever reached without it) ──
    if (!editCandidate && !cvFile) {
      toast.error("Upload the candidate's resume (PDF) first — a candidate can't be added without one.");
      return;
    }
    // ── LinkedIn URL is mandatory ────────────────────────────────────────
    if (!String(form.linkedInUrl || '').trim()) {
      toast.error('LinkedIn URL is required.');
      return;
    }
    // ── Requirement is mandatory on a new candidate ──────────────────────
    if (!editCandidate && !form.requirementId) {
      toast.error('Please select a requirement before adding the candidate.');
      return;
    }
    // ── JD email is mandatory when the requirement carries a job description ──
    if (jdEmailRequired && !sendJdEmail) {
      toast.error('This requirement has a job description — sending the JD email to the candidate is required.');
      return;
    }
    // ── Checklist validation (frontend guard — backend also validates) ────
    if (!editCandidate && effectiveChecklist.length > 0) {
      const unchecked = effectiveChecklist.filter(item => !checkedItems.has(item.id));
      if (unchecked.length > 0) {
        toast.error(`Please check all required items before saving.`);
        return;
      }
    }
    // ── Resume mandatory on edit ─────────────────────────────────────────
    if (editCandidate && !pendingResume.name) {
      toast.error('Please upload the candidate\'s resume (PDF) before saving.');
      return;
    }
    setSaving(true);
    try {
      let savedId = editCandidate?.id;

      // Combine country code + digits into a single phone string for storage
      const phoneForSave = form.phone
        ? `${form.phoneCountryCode || '+91'}${form.phone}`
        : '';

      // Combine yrs+mos → single float before sending to backend
      const experienceForSave = combineExp(form.experienceYrs, form.experienceMos);

      if (editCandidate) {
        await candidateAPI.update(editCandidate.id, { ...form, phone: phoneForSave, experience: experienceForSave });
        toast.success('Candidate updated!');
      } else {
        // Attach checklist snapshot to the payload
        const payload = {
          ...form,
          phone: phoneForSave,
          experience: experienceForSave,
          checklistSnapshot: effectiveChecklist.length > 0
            ? effectiveChecklist.filter(i => checkedItems.has(i.id))
            : null,
        };
        // Profile + resume go in ONE request: if the resume can't be stored the
        // candidate isn't created either, and the error below keeps the form
        // (and the chosen PDF) open so the recruiter can simply press Add again.
        const { data: created } = await candidateAPI.create(payload, cvFile);
        savedId = created?.candidate?.id;

        toast.success('Candidate added!');

        // Send JD email directly via dedicated endpoint — no template DB lookup
        // needed. Only when the requirement actually has a JD; no JD → never send.
        if (sendJdEmail && selectedReqHasJd && form.requirementId && savedId) {
          try {
            await emailAPI.sendJd({ candidateId: savedId, requirementId: form.requirementId });
            toast.success('Job description email sent to candidate!');
          } catch {
            toast.error('Candidate saved but JD email failed to send.');
          }
        }
      }

      setModalOpen(false);
      load();
    } catch (err) {
      const resp = err.response?.data;
      if (resp?.duplicate) {
        // Duplicate — surface the warning banner instead of a toast
        setDuplicateWarning({ candidate: resp.candidate, matchedBy: resp.matchedBy || 'email' });
      } else if (!err.response) {
        toast.error('No response from the server. Press Save again — if it did go through, you will see a duplicate warning instead.');
      } else {
        toast.error(resp?.message || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleDelete = async (id) => {
    setConfirmDelete(id);
  };

  const handleDeleteConfirmed = async () => {
    const id = confirmDelete;
    setConfirmDelete(null);
    try {
      await candidateAPI.delete(id);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  // ── Scroll-lock: prevent body scroll when any overlay is open ────────────
  const anyOverlayOpen = modalOpen || historyPanel.open || globalHistory || !!resubmitCandidate || !!confirmDelete;
  useLayoutEffect(() => {
    if (anyOverlayOpen) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [anyOverlayOpen]);
  // ─────────────────────────────────────────────────────────────────────────

  const openResubmit = (c, e) => {
    e.stopPropagation();
    setResubmitCandidate(c);
    setResubmitEditForm({
      fullName:            c.fullName || '',
      email:               c.email || '',
      phone:               c.phone || '',
      currentCompany:      c.currentCompany || '',
      currentRole:         c.currentRole || '',
      linkedInUrl:         c.linkedInUrl || '',
      location:            c.location || '',
      preferredLocation:   c.preferredLocation || '',
      experience:          c.experience ?? '',
      experienceYrs:       splitExp(c.experience).yrs,
      experienceMos:       splitExp(c.experience).mos,
      expectedSalary:      c.expectedSalary || '',
      expectedSalaryCurrency: c.expectedSalaryCurrency || 'INR',
      currentSalary:        c.currentSalary  || '',
      currentSalaryCurrency: c.currentSalaryCurrency  || 'INR',
      servingNoticePeriod: c.servingNoticePeriod || false,
      availabilityToInterview: c.availabilityToInterview || '',
      offerInHand:         c.offerInHand || false,
      offerCompany:        c.offerCompany || '',
      offerPackage:        c.offerPackage || '',
      offerDoj:            c.offerDoj ? c.offerDoj.slice(0, 10) : '',
      skills:              Array.isArray(c.skills) ? c.skills.join(', ') : (c.skills || ''),
      experienceSummary:   c.experienceSummary || '',
      noticePeriodNotes:   c.noticePeriodNotes || '',
      notes:               c.notes || '',
    });
    setResubmitReqId('');
    setResubmitNotes('');
    setResubmitClientNotes('');
    setResubmitBillingCost('');
    setResubmitBillingCurrency('USD');
    setResubmitBillingType('');
  };

  const handleResubmit = async () => {
    if (!resubmitCandidate.resumeName) {
      return toast.error(`${resubmitCandidate.fullName} has no resume on file — upload it first (Edit), then resubmit.`);
    }
    if (!resubmitReqId) return toast.error('Please select a requirement.');
    if (!resubmitEditForm.fullName?.trim()) return toast.error('Full name is required.');
    const req = allRequirements.find(r => r.id === resubmitReqId);
    if (!req) return toast.error('Requirement not found.');
    setResubmitting(true);
    try {
      // Submissions only carry client-facing notes, so the modal's "Internal
      // Notes" are appended to the candidate's own internal notes, labelled
      // with the requirement they were written for.
      let notes = resubmitEditForm.notes;
      if (htmlToText(resubmitNotes).trim()) {
        const title = String(req.jobTitle || '').replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
        notes = `${notes || ''}<p><strong>Resubmitted to ${title}:</strong></p>${resubmitNotes}`;
      }
      // Save updated candidate details first
      await candidateAPI.update(resubmitCandidate.id, {
        ...resubmitEditForm,
        notes,
        experience: combineExp(resubmitEditForm.experienceYrs, resubmitEditForm.experienceMos),
        skills: resubmitEditForm.skills
          ? resubmitEditForm.skills.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
          : [],
      });
      // Then create submission
      await submissionAPI.create({
        candidateId:             resubmitCandidate.id,
        clientId:                req.clientId,
        requirementId:           resubmitReqId,
        notes:                   resubmitClientNotes || null,
        overrideBillingCost:     resubmitBillingCost     || null,
        overrideBillingCurrency: resubmitBillingCurrency || null,
        overrideBillingType:     resubmitBillingType     || null,
      });
      toast.success(`${resubmitCandidate.fullName} submitted to "${req.jobTitle}"`);
      setResubmitCandidate(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed.');
    } finally {
      setResubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Candidates</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{total} total candidates</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setGlobalHistory(true)} tooltip="View all candidate changes"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> All History</Button>
          {canCreate && <Button onClick={openCreate}>+ Add Candidate</Button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Input placeholder="Search name, email, skills…" value={searchInput}
          onChange={e => { setSearchInput(e.target.value); setPage(1); }} className="w-full sm:w-auto sm:max-w-xs" />
        {isSuperAdmin() && (
          <SearchableSelect
            value={filters.clientId}
            onChange={(val) => {
              // Clear the requirement filter when client changes — the prior
              // requirement may belong to a different client.
              setFilters({ ...filters, clientId: val, requirementId: '' });
              setPage(1);
            }}
            placeholder="All Clients"
            className="w-full sm:w-52"
            options={clients.map(c => ({ value: c.id, label: c.clientName, sub: c.industry || '' }))}
          />
        )}
        {/* Requirement Status filter — Open / On Hold / Closed.
            Cascades into the Requirement dropdown below: selecting a status
            narrows the requirement options to only those matching, and
            clears any previously selected requirement so the user isn't left
            with a stale picked requirement that no longer matches the
            filter. Backend filters candidates whose submissions link to
            requirements with this status. */}
        <SearchableSelect
          value={filters.requirementStatus}
          onChange={(val) => {
            setFilters({ ...filters, requirementStatus: val, requirementId: '' });
            setPage(1);
          }}
          placeholder="All Statuses"
          className="w-full sm:w-44"
          options={[
            { value: 'Open',    label: 'Open'    },
            { value: 'On Hold', label: 'On Hold' },
            { value: 'Closed',  label: 'Closed'  },
          ]}
        />
        {/* Requirement filter — visible to all roles. Cascade chain:
            client → status → requirement. Options narrow on each step. */}
        <SearchableSelect
          value={filters.requirementId}
          onChange={(val) => { setFilters({ ...filters, requirementId: val }); setPage(1); }}
          placeholder="All Requirements"
          className="w-full sm:w-52"
          options={allRequirements
            .filter(r => !filters.clientId         || (r.clientId || r.client?.id) === filters.clientId)
            .filter(r => !filters.requirementStatus || r.status === filters.requirementStatus)
            .map(r => ({ value: r.id, label: r.jobTitle, sub: r.client?.clientName || '' }))}
        />
        {/* Recruiter filter — who added the candidate. Options come from the
            list response, so they match the names actually on the page. */}
        {canFilterByRecruiter && authors.length > 0 && (
          <SearchableSelect
            value={filters.recruiterId}
            onChange={(val) => { setFilters({ ...filters, recruiterId: val }); setPage(1); }}
            placeholder="All Recruiters"
            className="w-full sm:w-52"
            options={authorOptions(authors)}
          />
        )}
        <Input type="text" inputMode="decimal" placeholder="Min Exp" value={filters.minExp}
          onChange={e => setFilters({ ...filters, minExp: onlyDecimal(e.target.value) })} className="flex-1 min-w-0 sm:flex-none sm:w-28" />
        <Input type="text" inputMode="decimal" placeholder="Max Exp" value={filters.maxExp}
          onChange={e => setFilters({ ...filters, maxExp: onlyDecimal(e.target.value) })} className="flex-1 min-w-0 sm:flex-none sm:w-28" />
      </div>

      {loading ? <div className="py-16 flex justify-center"><Spinner /></div> : candidates.length === 0 ? (
        <EmptyState icon="👤" title="No candidates found" message="Add your first candidate."
          action={canCreate && <Button onClick={openCreate}>Add Candidate</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {candidates.map(c => (
            <div
              key={c.id}
              onClick={() => navigate(`/candidate-profile/${c.id}`, { state: { from: '/candidates', backLabel: 'Back to Candidates' } })}
              style={{
                background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
                borderRadius: 14, padding: 20, boxShadow: 'var(--shadow-card)',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-brand)'; e.currentTarget.style.boxShadow = isDark ? '0 4px 20px rgba(0,0,0,0.25)' : '0 4px 16px rgba(0,0,0,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
            >
              <div className="flex items-start justify-between mb-3 gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex-shrink-0" style={{ width: 40, height: 40, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 15, boxShadow: '0 2px 8px rgba(99,102,241,0.3)' }}>
                    {c.fullName?.[0]}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.fullName}</p>
                    <p className="truncate" style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>{c.email}</p>
                  </div>
                </div>
                {/* Submissions count badge */}
                {c.submissions?.length > 0 && (
                  <span className="whitespace-nowrap flex-shrink-0" style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99,
                    background: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff',
                    color: isDark ? '#a5b4fc' : '#4f46e5',
                    border: `1px solid ${isDark ? 'rgba(99,102,241,0.25)' : '#c7d2fe'}`,
                  }}>
                    {c.submissions.length} submission{c.submissions.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }} className="space-y-1 mb-3">
                <p>💼 {(() => {
                  const n = parseFloat(c.experience);
                  if (isNaN(n) || n < 0) return `${c.experience || 0} yrs`;
                  const yrs = Math.floor(n); const mos = Math.round((n - yrs) * 12);
                  if (yrs > 0 && mos > 0) return `${yrs} yrs ${mos} months`;
                  if (yrs > 0) return `${yrs} yrs`;
                  if (mos > 0) return `${mos} months`;
                  return '0 yrs';
                })()} · {c.currentRole || 'N/A'}</p>
                {c.location && <p>📍 {c.location}{c.preferredLocation && c.preferredLocation !== c.location ? ` → ${c.preferredLocation}` : ''}</p>}
                {c.noticePeriod != null && (
                  <p>⏱ Notice: {c.noticePeriod === 0 ? 'Immediate' : `${c.noticePeriod} days`}{c.offerInHand ? ' · 🤝 Offer in hand' : ''}</p>
                )}
                {c.relevantExperience && (() => {
                  const n = parseFloat(c.relevantExperience);
                  const display = (!isNaN(n) && n > 0)
                    ? (() => { const y = Math.floor(n); const m = Math.round((n - y) * 12); return y > 0 && m > 0 ? `${y} yrs ${m} months` : y > 0 ? `${y} yrs` : `${m} months`; })()
                    : c.relevantExperience;
                  return <p style={{ color: 'var(--text-muted)' }} className="truncate" title={display}>🔧 {display}</p>;
                })()}
                {c.createdByUser && (
                  <p style={{ color: 'var(--text-muted)' }}>
                    👤 Recruited by <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                      {formatActor(c.createdByUser, c.createdByImpersonator, showAttribution)}
                    </span>
                  </p>
                )}
              </div>

              {c.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {c.skills.slice(0, 4).map(skill => (
                    <span key={skill} style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 99,
                      background: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff',
                      color: isDark ? '#a5b4fc' : '#4f46e5',
                      border: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : '#c7d2fe'}`,
                    }}>{skill}</span>
                  ))}
                  {c.skills.length > 4 && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>+{c.skills.length - 4}</span>}
                </div>
              )}

              {/* Resume badge */}
              {c.resumeName && (
                <div className="mb-3" onClick={e => e.stopPropagation()}>
                  <Button variant="outline" size="xs" style={{ borderRadius: 99 }}
                    loading={loadingResume === c.id}
                    loadingText="Opening…"
                    onClick={async () => {
                      setLoadingResume(c.id);
                      try {
                        const { data } = await candidateAPI.getResumeUrl(c.id);
                        // No `|| /candidates/:id/resume` fallback: that URL cannot
                        // carry the JWT and broke behind two pods. An absent url
                        // means there is genuinely no CV — say so.
                        if (!data?.url) { toast.error('No resume on file for this candidate.'); return; }
                        window.open(data.url, '_blank', 'noopener,noreferrer');
                      } catch (err) {
                        toast.error(err?.response?.status === 404
                          ? 'No resume on file for this candidate.'
                          : 'Could not open the resume. Please try again.');
                      }
                      finally { setLoadingResume(null); }
                    }}
                  >
                    📄 View Resume
                  </Button>
                </div>
              )}

              <div
                style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}
                className="flex gap-2 flex-wrap"
                onClick={e => e.stopPropagation()}
              >
                <Button variant="outline" size="xs" onClick={() => setHistoryPanel({ open: true, id: c.id, name: c.fullName })} tooltip="View activity history">🕐 History</Button>
                {(canEdit || (isDeveloper && c.createdById === user?.id) || (isRecruiter && c.createdById === user?.id)) && (
                  <Button variant="teal" size="xs" onClick={(e) => openResubmit(c, e)} tooltip="Submit to another requirement">🔁 Resubmit</Button>
                )}
                {(canEdit || (isDeveloper && c.createdById === user?.id && c.createdByRole === 'Developer') || (isRecruiter && c.createdById === user?.id)) && (
                  <Button variant="secondary" size="xs"
                    loading={loadingEdit === c.id} loadingText="Loading…"
                    onClick={() => openEdit(c)}>✏️ Edit</Button>
                )}
                {canEmail && (
                  <Button variant="success" size="xs" onClick={() => navigate('/emails', { state: { compose: 'client', candidate: c } })} tooltip="Email to Client">✉ Email to Client</Button>
                )}
                {(canDelete || (isDeveloper && c.createdById === user?.id && c.createdByRole === 'Developer')) && (
                  <div style={{ marginLeft: 'auto' }} onClick={(e) => e.stopPropagation()}>
                    <Button variant="danger" size="xs" onClick={() => handleDelete(c.id)}>Delete</Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination page={page} pages={pages} onPage={setPage} />

      <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); setDuplicateWarning(null); dupFieldsRef.current = { email: '', phone: '' }; }}
        title={editCandidate ? 'Edit Candidate' : 'Add Candidate'} size="lg">
        <form onSubmit={handleSave} className="space-y-5">

          {/* ── Resume-first create flow ─────────────────────────────────────
              The resume is MANDATORY on create, so the form is gated behind it:
              Step 1 shows only the dropzone; the fields appear once a PDF is
              attached (auto-filled where the parser finds data). A scanned /
              unparseable PDF still unlocks the form — the resume is attached,
              the recruiter just types the details. No resume → no candidate. */}
          {!editCandidate && (
            <div className="space-y-3">
              <input id="cv-file-input" type="file" accept="application/pdf" className="hidden"
                onChange={e => { const f = e.target.files[0]; if (f) handleCvFile(f); e.target.value = ''; }} />

              {/* Step 1 — the gate */}
              {!cvExtracted && !parsing && (
                <div
                  onDragOver={e => { e.preventDefault(); setCvDragOver(true); }}
                  onDragLeave={() => setCvDragOver(false)}
                  onDrop={e => {
                    e.preventDefault(); setCvDragOver(false);
                    const file = e.dataTransfer.files[0];
                    if (file) handleCvFile(file);
                  }}
                  onClick={() => document.getElementById('cv-file-input').click()}
                  style={{
                    border: `2px dashed ${cvDragOver ? (isDark ? '#818cf8' : '#6366f1') : 'var(--border-subtle)'}`,
                    borderRadius: 14, padding: '44px 24px', textAlign: 'center',
                    background: cvDragOver
                      ? (isDark ? 'rgba(99,102,241,0.08)' : '#eef2ff')
                      : (isDark ? 'rgba(99,102,241,0.03)' : 'var(--bg-raised)'),
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}>
                  <div style={{ fontSize: 44, marginBottom: 10 }}>📄</div>
                  <p style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                    Start with the candidate's resume
                  </p>
                  <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 18 }}>
                    Drop the PDF here or click to browse · Max 5 MB · required to add a candidate
                  </p>
                  {/* The three-step promise */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {[['1', '📎 Attach resume'], ['2', '⚡ Details auto-fill'], ['3', '✅ Review & save']].map(([n, label]) => (
                      <span key={n} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
                        background: 'var(--surface-3)', border: '1px solid var(--border-subtle)',
                        borderRadius: 99, padding: '5px 12px',
                      }}>
                        <span style={{
                          width: 16, height: 16, borderRadius: '50%', fontSize: 10, fontWeight: 700,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff',
                        }}>{n}</span>
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Parsing */}
              {parsing && (
                <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 14, padding: '36px 24px', textAlign: 'center', background: 'var(--bg-raised)' }}>
                  <div style={{ fontSize: 34, marginBottom: 10 }}>⚡</div>
                  <p style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>Reading the resume…</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>Name, contact, skills and experience are being extracted</p>
                </div>
              )}

              {/* Step 2 — attached chip (Replace keeps the flow; Remove re-gates) */}
              {cvExtracted && !parsing && cvFile && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  background: isDark ? 'rgba(16,185,129,0.08)' : '#f0fdf4',
                  border: `1px solid ${isDark ? 'rgba(16,185,129,0.3)' : '#a7f3d0'}`,
                  borderRadius: 10, padding: '10px 14px',
                }}>
                  <span style={{ fontSize: 20 }}>✅</span>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#6ee7b7' : '#047857' }}>
                      Resume attached — {cvFile.name}
                    </p>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      Saved with the candidate automatically. Review the auto-filled form below.
                    </p>
                  </div>
                  <Button type="button" variant="secondary" size="xs" onClick={() => document.getElementById('cv-file-input').click()}>↻ Replace</Button>
                  <Button type="button" variant="danger" size="xs" onClick={() => { setCvFile(null); setCvExtracted(false); setPendingResume({ name: null, size: null }); }}>✕ Remove</Button>
                </div>
              )}
            </div>
          )}

          {/* Fields + footer stay hidden on create until the resume is attached */}
          {(!!editCandidate || cvExtracted) && (<>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Full Name *" value={form.fullName}
              onChange={e => setForm({ ...form, fullName: e.target.value })} required />
            <Input label="Email *" type="email" value={form.email}
              onChange={handleEmailChange} required />
          </div>

          {/* ── Duplicate candidate warning ── */}
          {duplicateWarning && (
            <div
              className="flex items-center justify-between gap-3 rounded-xl px-4 py-3"
              style={{
                background: isDark ? 'rgba(245,158,11,0.1)' : '#fef9c3',
                border: `1px solid ${isDark ? 'rgba(245,158,11,0.35)' : '#fde047'}`,
              }}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="text-lg flex-shrink-0">⚠️</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold" style={{ color: isDark ? '#fcd34d' : '#92400e' }}>
                    {editCandidate ? 'Duplicate detected' : 'Candidate already exists'}
                  </p>
                  <p className="text-xs break-words" style={{ color: isDark ? '#fbbf24' : '#b45309', marginTop: 2, overflowWrap: 'anywhere' }}>
                    <strong>{duplicateWarning.candidate.fullName}</strong>
                    {editCandidate
                      ? ` already uses this ${duplicateWarning.matchedBy === 'phone' ? 'phone number' : 'email'}.`
                      : ` is already registered with this ${duplicateWarning.matchedBy === 'phone' ? 'phone number' : 'email'}.`}
                    {duplicateWarning.matchedBy === 'phone' && duplicateWarning.candidate.email && (
                      <span className="ml-1 opacity-70">({duplicateWarning.candidate.email})</span>
                    )}
                  </p>
                </div>
              </div>
              <Button type="button" variant="warning" size="xs"
                onClick={() => {
                  setModalOpen(false);
                  setDuplicateWarning(null);
                  navigate(`/candidate-profile/${duplicateWarning.candidate.id}`, {
                    state: { from: '/candidates', backLabel: 'Back to Candidates' },
                  });
                }}
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >View existing →</Button>
            </div>
          )}

          {/* Phone — half width (same as before) */}
          <div className="w-full sm:w-1/2 sm:pr-2">
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>
                Phone
              </label>
              <div className="flex gap-1.5">
                <CountryCodePicker
                  value={form.phoneCountryCode || '+91'}
                  iso={form.phoneIso || 'in'}
                  isDark={isDark}
                  onChange={(code, iso) => setForm(prev => ({ ...prev, phoneCountryCode: code, phoneIso: iso }))}
                />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={handlePhoneChange}
                  maxLength={10}
                  placeholder="10-digit number"
                  style={{
                    flex: 1, minWidth: 0, background: 'var(--bg-input)', border: '1px solid var(--border-subtle)',
                    color: 'var(--text-primary)', borderRadius: 8, padding: '8px 12px',
                    fontSize: 13, outline: 'none',
                  }}
                />
              </div>
              {form.phone && form.phone.replace(/\D/g, '').length > 0 && form.phone.replace(/\D/g, '').length < 10 && (
                <p style={{ fontSize: 11, color: isDark ? '#f87171' : '#dc2626', marginTop: 4 }}>
                  Phone must be 10 digits
                </p>
              )}
            </div>
          </div>

          {/* Total Experience — full width, same style as Relevant Experience */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>
              Total Experience *
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, border: '1px solid var(--border-subtle)', borderRadius: 8, background: 'var(--bg-input)', padding: '0 10px' }}>
                <input
                  type="text" inputMode="numeric" placeholder="0"
                  value={form.experienceYrs}
                  onChange={e => setForm(p => ({ ...p, experienceYrs: onlyDigits(e.target.value) }))}
                  style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', color: 'var(--text-primary)', padding: '8px 0', fontSize: 14, outline: 'none' }}
                />
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>yrs</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, border: '1px solid var(--border-subtle)', borderRadius: 8, background: 'var(--bg-input)', padding: '0 10px' }}>
                <input
                  type="text" inputMode="numeric" placeholder="0"
                  value={form.experienceMos}
                  onChange={e => setForm(p => ({ ...p, experienceMos: onlyDigits(e.target.value) }))}
                  style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', color: 'var(--text-primary)', padding: '8px 0', fontSize: 14, outline: 'none' }}
                />
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>mos</span>
              </div>
            </div>
          </div>

          {/* Relevant Experience Summary — replaces the old numeric yrs/mos
              "Relevant Experience" inputs so HR writes it up exactly once. */}
          <RichTextEditor label="Relevant Experience Summary" value={form.experienceSummary} rows={4}
            placeholder="Write-up of the candidate's relevant experience — domains, key projects, team size, achievements… (usable in client emails as {{experience_summary}})"
            onChange={(html) => setForm({ ...form, experienceSummary: html })} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Current Company" value={form.currentCompany}
              onChange={e => setForm({ ...form, currentCompany: e.target.value })} />
            <Input label="Current Role" value={form.currentRole}
              onChange={e => setForm({ ...form, currentRole: e.target.value })} />
          </div>
          <div style={{ position: 'relative' }}>
            <Input
              label="LinkedIn URL *"
              placeholder="https://www.linkedin.com/in/username"
              value={form.linkedInUrl}
              onChange={e => setForm({ ...form, linkedInUrl: e.target.value })}
              style={{ paddingRight: form.linkedInUrl ? 72 : undefined }}
            />
            {form.linkedInUrl && (
              <a
                href={form.linkedInUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ position: 'absolute', right: 10, top: 30, color: '#0a66c2', fontSize: 13, fontWeight: 500, background: 'var(--bg-input)', padding: '2px 4px', borderRadius: 4, pointerEvents: 'auto' }}
              >
                Open ↗
              </a>
            )}
          </div>
          <Input
            label="Availability to Interview"
            placeholder="e.g. Weekday evenings, After 5 PM, 12 Aug onward"
            value={form.availabilityToInterview}
            onChange={e => setForm({ ...form, availabilityToInterview: e.target.value })}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Current Location" value={form.location}
              onChange={e => setForm({ ...form, location: e.target.value })} />
            <Input label="Preferred Location" value={form.preferredLocation}
              onChange={e => setForm({ ...form, preferredLocation: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Current Salary with dynamic currency */}
            <div className="flex flex-col gap-1.5">
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Current Salary (LPA)</label>
              <div className="flex items-stretch gap-0" style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden', background: 'var(--input-bg)' }}>
                <select
                  value={form.currentSalaryCurrency}
                  onChange={e => setForm({ ...form, currentSalaryCurrency: e.target.value })}
                  style={{
                    background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    border: 'none', borderRight: '1px solid var(--border-subtle)',
                    color: 'var(--text-primary)', fontSize: 13, fontWeight: 600,
                    padding: '0 10px', cursor: 'pointer', outline: 'none',
                    minWidth: 72,
                  }}
                >
                  {['USD','INR','EUR','GBP','AED','SGD','AUD','CAD','JPY','MYR','QAR','SAR'].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.currentSalary}
                  onChange={e => setForm({ ...form, currentSalary: onlyDecimal(e.target.value) })}
                  placeholder="e.g. 60000"
                  style={{
                    flex: 1, background: 'transparent', border: 'none', outline: 'none',
                    color: 'var(--text-primary)', fontSize: 13, padding: '9px 12px',
                  }}
                />
              </div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Enter in LPA (Lakhs Per Annum). A raw amount is auto-read as LPA — e.g. 1500000 shows as 15 LPA.
              </span>
            </div>

            {/* Expected Salary with dynamic currency */}
            <div className="flex flex-col gap-1.5">
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Expected Salary (LPA)</label>
                <div className="flex items-stretch gap-0" style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden', background: 'var(--input-bg)' }}>
                  {/* Currency selector */}
                  <select
                    value={form.expectedSalaryCurrency}
                    onChange={e => setForm({ ...form, expectedSalaryCurrency: e.target.value })}
                    style={{
                      background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                      border: 'none', borderRight: '1px solid var(--border-subtle)',
                      color: 'var(--text-primary)', fontSize: 13, fontWeight: 600,
                      padding: '0 10px', cursor: 'pointer', outline: 'none',
                      minWidth: 72,
                    }}
                  >
                    {['USD','INR','EUR','GBP','AED','SGD','AUD','CAD','JPY','MYR','QAR','SAR'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {/* Amount input */}
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.expectedSalary}
                    onChange={e => setForm({ ...form, expectedSalary: onlyDecimal(e.target.value) })}
                    placeholder="e.g. 80000"
                    style={{
                      flex: 1, background: 'transparent', border: 'none', outline: 'none',
                      color: 'var(--text-primary)', fontSize: 13, padding: '9px 12px',
                    }}
                  />
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Enter in LPA (Lakhs Per Annum). A raw amount is auto-read as LPA — e.g. 1500000 shows as 15 LPA.
                </span>
              </div>

            {/* Notice period — the Fixed Days / Serving flag stays; the numeric
                days / last-working-date input it used to switch between was
                retired (details go in Notice Period Notes below, typed once). */}
            <div className="flex flex-col gap-1.5">
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                Notice Period
              </label>
              {/* Toggle pill */}
              <div className="flex items-center gap-2">
                <Button type="button" size="xs" variant={!form.servingNoticePeriod ? 'primary' : 'ghost'} onClick={() => setForm({ ...form, servingNoticePeriod: false })} style={{ borderRadius: 20 }}>Fixed Days</Button>
                <Button type="button" size="xs" variant={form.servingNoticePeriod ? 'warning' : 'ghost'} onClick={() => setForm({ ...form, servingNoticePeriod: true })} style={{ borderRadius: 20 }}>🕐 Serving</Button>
              </div>
            </div>
          </div>

          {/* Notice Period Notes — the single free-text home for notice details */}
          <RichTextEditor label="Notice Period Notes" value={form.noticePeriodNotes} rows={3}
            placeholder="e.g. 30 days, negotiable to 15 with buyout, serving — LWD 22 Aug… (usable in client emails as {{notice_period_notes}})"
            onChange={(html) => setForm({ ...form, noticePeriodNotes: html })} />

          {/* Offer In Hand — full-width toggle card — hidden for Developer role */}
          {!isDeveloper && <div
            onClick={() => setForm({ ...form, offerInHand: !form.offerInHand, offerCompany: '', offerPackage: '', offerDoj: '' })}
            className="flex items-center gap-3 cursor-pointer"
            style={{
              padding: '10px 14px', borderRadius: 10,
              border: `1px solid ${form.offerInHand ? (isDark ? 'rgba(99,102,241,0.4)' : '#c7d2fe') : 'var(--border-subtle)'}`,
              background: form.offerInHand ? (isDark ? 'rgba(99,102,241,0.08)' : '#eef2ff') : 'var(--bg-card)',
              transition: 'all 0.15s',
            }}
          >
            {/* Custom toggle switch */}
            <div style={{
              width: 36, height: 20, borderRadius: 10, position: 'relative', flexShrink: 0,
              background: form.offerInHand ? '#6366f1' : (isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'),
              transition: 'background 0.2s',
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 3,
                left: form.offerInHand ? 18 : 3,
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, color: form.offerInHand ? (isDark ? '#a5b4fc' : '#4f46e5') : 'var(--text-primary)', lineHeight: 1.3 }}>
                Offer In Hand
              </p>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                {form.offerInHand ? 'Fill in the offer details below' : 'Candidate has an existing offer from another company'}
              </p>
            </div>
          </div>}

          {/* Offer-in-hand details — shown only when checked, hidden for Developer */}
          {!isDeveloper && form.offerInHand && (
            <div
              style={{
                background: isDark ? 'rgba(234,179,8,0.06)' : 'rgba(234,179,8,0.06)',
                border: `1px solid ${isDark ? 'rgba(234,179,8,0.25)' : 'rgba(234,179,8,0.35)'}`,
                borderRadius: 10, padding: '14px 16px',
              }}
            >
              <p style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#fbbf24' : '#92400e', marginBottom: 12, letterSpacing: '0.03em' }}>
                🤝 OFFER IN HAND DETAILS
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Input
                  label="Company Name"
                  value={form.offerCompany}
                  onChange={e => setForm({ ...form, offerCompany: e.target.value })}
                  placeholder="e.g. Google"
                />
                <Input
                  label="Package Offered"
                  value={form.offerPackage}
                  onChange={e => setForm({ ...form, offerPackage: e.target.value })}
                  placeholder="e.g. 18 LPA"
                />
                <Input
                  label="Date of Joining"
                  type="date"
                  value={form.offerDoj}
                  onChange={e => setForm({ ...form, offerDoj: e.target.value })}
                />
              </div>
            </div>
          )}

          <Input label="Skills (comma-separated) *" value={form.skills}
            onChange={e => setForm({ ...form, skills: e.target.value })}
            placeholder="react, javascript, css…" />

          <RichTextEditor label="Internal Notes" value={form.notes} rows={5}
            placeholder="Anything the team should know about this candidate…"
            onChange={(html) => setForm({ ...form, notes: html })} />

          {/* ── First Submission section (create mode only) ── */}
          {!editCandidate && (
            <div style={{
              border: `1px solid ${isDark ? 'rgba(99,102,241,0.25)' : '#c7d2fe'}`,
              borderRadius: 12,
              background: isDark ? 'rgba(99,102,241,0.05)' : '#f5f3ff',
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}>

              {/* Requirement */}
              <div className="flex flex-col gap-2">
                <SearchableSelect
                  label={loadingReqs ? 'Requirement (loading…)' : 'Requirement'}
                  required={!editCandidate}
                  value={form.requirementId}
                  onChange={handleRequirementChange}
                  disabled={loadingReqs}
                  placeholder="— Select a requirement —"
                  options={allRequirements.map(r => ({
                    value: r.id,
                    label: r.jobTitle,
                    sub:   `${r.client?.clientName || ''}${r.minExperience != null ? ` · ${r.minExperience}–${r.maxExperience} yrs` : ''}${r.status !== 'Open' ? ` · ${r.status}` : ''}`,
                  }))}
                />
                {form.requirementId && (() => {
                  const req = allRequirements.find(r => r.id === form.requirementId);
                  return req?.client?.clientName ? (
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      🏢 <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{req.client.clientName}</span>
                      {req.status !== 'Open' && <span style={{ color: '#f59e0b', marginLeft: 4 }}>· {req.status}</span>}
                    </p>
                  ) : null;
                })()}
              </div>

              {/* Override Billing Rate */}
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                  Override Billing Rate
                </p>
                <div className="flex flex-wrap gap-2">
                  <Select
                    value={form.overrideBillingCurrency}
                    onChange={e => setForm({ ...form, overrideBillingCurrency: e.target.value })}
                    className="w-full sm:w-auto"
                    style={{ minWidth: 90 }}
                  >
                    {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                  </Select>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="Amount"
                    value={form.overrideBillingCost}
                    onChange={e => setForm({ ...form, overrideBillingCost: onlyDecimal(e.target.value) })}
                    className="w-full sm:flex-1"
                    style={{ flex: '1 1 120px', minWidth: 0 }}
                  />
                  <Select
                    value={form.overrideBillingType}
                    onChange={e => setForm({ ...form, overrideBillingType: e.target.value })}
                    className="w-full sm:w-auto"
                    style={{ minWidth: 120 }}
                  >
                    <option value="">Type</option>
                    {BILLING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </div>
              </div>

              {/* Client Notes */}
              <RichTextEditor
                label="Client Notes"
                value={form.clientNotes}
                rows={4}
                onChange={(html) => setForm({ ...form, clientNotes: html })}
                placeholder="Visible to client in batch email…"
              />

              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
                💡 Selecting a requirement will auto-create a submission with status "Applied". Billing &amp; client notes will be attached to that submission.
              </p>
            </div>
          )}

          {editCandidate ? (
            <div>
              <ResumeUploader
                candidateId={editCandidate.id}
                resumeName={pendingResume.name}
                resumeSize={pendingResume.size}
                onUploaded={(name, size) => {
                  setPendingResume({ name, size });
                  // The upload is saved on its own, even if this modal is then
                  // cancelled — reflect it in the list so Resubmit sees the resume.
                  setCandidates(prev => prev.map(x => (x.id === editCandidate.id ? { ...x, resumeName: name, resumeSize: size } : x)));
                }}
                onParsed={(extracted) => {
                  setForm(prev => {
                    const updated = { ...prev };
                    const fill = (key, val) => { if (val != null && val !== '' && !prev[key]) updated[key] = val; };
                    fill('fullName',          extracted.fullName);
                    fill('email',             extracted.email);
                    fill('phone',             extracted.phone);
                    fill('location',          extracted.location);
                    fill('currentCompany',    extracted.currentCompany);
                    fill('currentRole',       extracted.currentRole);
                    fill('expectedSalary',    extracted.expectedSalary);
                    fill('preferredLocation', extracted.preferredLocation);
                    if (extracted.skills?.length && (!prev.skills || prev.skills.length === 0)) updated.skills = extracted.skills;
                    if (extracted.experience != null && !prev.experienceYrs && !prev.experienceMos) {
                      const { yrs, mos } = splitExp(extracted.experience);
                      updated.experienceYrs = yrs;
                      updated.experienceMos = mos;
                    }
                    const filled = Object.keys(updated).filter(k => updated[k] !== prev[k]).length;
                    if (filled > 0) toast.success(`${filled} field${filled > 1 ? 's' : ''} auto-filled from CV!`);
                    return updated;
                  });
                }}
              />
              {!pendingResume.name && (
                <p style={{ fontSize: 11, color: isDark ? '#f87171' : '#dc2626', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  ⚠ Resume is required — upload a PDF to save.
                </p>
              )}
            </div>
          ) : (
            /* In create mode the resume is handled by the CV upload zone above */
            cvFile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: isDark ? '#6ee7b7' : '#047857' }}>
                📎 <strong>{cvFile.name}</strong> will be saved automatically
              </div>
            ) : (
              <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: 12, fontSize: 12, color: isDark ? '#f87171' : '#dc2626' }}>
                ⛔ No resume attached — upload the candidate's PDF above; the candidate cannot be saved without it.
              </div>
            )
          )}

          {/* ── Send JD Email checkbox ──────────────────────────────────────── */}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 12, borderRadius: 12,
            border: `1px solid ${sendJdEmail ? (isDark ? 'rgba(99,102,241,0.5)' : '#a5b4fc') : 'var(--border-subtle)'}`,
            background: sendJdEmail ? (isDark ? 'rgba(99,102,241,0.08)' : '#eef2ff') : 'var(--bg-raised)',
            padding: 16, cursor: (jdEmailRequired || jdEmailBlocked) ? 'not-allowed' : 'pointer', transition: 'all 0.15s ease',
            opacity: sendJdEmail ? 1 : (jdEmailBlocked ? 0.55 : 0.65),
          }}>
            <input
              type="checkbox"
              checked={sendJdEmail}
              disabled={jdEmailRequired || jdEmailBlocked}
              onChange={e => { if (!jdEmailRequired && !jdEmailBlocked) setSendJdEmail(e.target.checked); }}
              className="w-4 h-4 mt-0.5 rounded accent-indigo-500 flex-shrink-0"
            />
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                📧 Send Job Description Email to Candidate
                {jdEmailRequired
                  ? <span style={{ marginLeft: 8, fontSize: 11, color: '#dc2626', fontWeight: 600 }}>(required — this requirement has a JD)</span>
                  : jdEmailBlocked
                    ? <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280', fontWeight: 600 }}>(no job description on this requirement)</span>
                    : (!editCandidate && <span style={{ marginLeft: 8, fontSize: 11, color: '#6366f1', fontWeight: 400 }}>(recommended for new candidates)</span>)}
              </p>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>
                {!form.requirementId
                  ? 'Select a requirement above — email will include full job description, skills, and details'
                  : jdEmailBlocked
                    ? `"${allRequirements.find(r => r.id === form.requirementId)?.jobTitle || 'This requirement'}" has no job description — nothing to send.`
                    : `Will send JD for "${allRequirements.find(r => r.id === form.requirementId)?.jobTitle || 'selected role'}" to ${form.email || 'candidate'}`}
              </p>
              {form.requirementId && selectedReqHasJd && (
                <p style={{ fontSize: 11, color: isDark ? '#a5b4fc' : '#6366f1', marginTop: 4 }}>
                  ✅ Ready — includes position, location, experience, skills &amp; full job description
                </p>
              )}
              {jdEmailRequired && (
                <p style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                  🔒 A job description is on file for this role — the candidate must receive it on first submission.
                </p>
              )}
              {jdEmailBlocked && (
                <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                  🚫 This requirement has no job description, so the JD email can't be sent. Add a JD to the requirement to enable it.
                </p>
              )}
              {sendJdEmail && !form.requirementId && (
                <p style={{ fontSize: 11, color: '#d97706', marginTop: 4 }}>⚠ No requirement selected — email won't be sent even if checked</p>
              )}
            </div>
          </label>

          {/* ── Candidate Submission Checklist (global + per-requirement) ───── */}
          {effectiveChecklist.length > 0 && (
            <div style={{
              borderRadius: 12,
              border: `1px solid ${
                !editCandidate && effectiveChecklist.some(i => !checkedItems.has(i.id))
                  ? (isDark ? 'rgba(239,68,68,0.35)' : '#fecaca')
                  : (isDark ? 'rgba(16,185,129,0.3)' : '#a7f3d0')
              }`,
              background: !editCandidate && effectiveChecklist.some(i => !checkedItems.has(i.id))
                ? (isDark ? 'rgba(239,68,68,0.05)' : '#fef2f2')
                : (isDark ? 'rgba(16,185,129,0.05)' : '#f0fdf4'),
              padding: 16,
            }}>
              <div className="flex items-center gap-2 mb-3">
                <span style={{ fontSize: 16 }}>✅</span>
                <p style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#34d399' : '#065f46' }}>
                  Submission Checklist
                  {editCandidate
                    ? <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>(confirmed at submission — read only)</span>
                    : <span style={{ fontSize: 11, fontWeight: 400, color: isDark ? '#f87171' : '#dc2626', marginLeft: 8 }}>* All items required</span>
                  }
                </p>
              </div>
              <div className="space-y-2">
                {effectiveChecklist.map(item => {
                  const isChecked = checkedItems.has(item.id);
                  const isReadOnly = !!editCandidate;
                  return (
                    <label
                      key={item.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px', borderRadius: 8,
                        cursor: isReadOnly ? 'default' : 'pointer',
                        border: `1px solid ${isChecked
                          ? (isDark ? 'rgba(16,185,129,0.3)' : '#bbf7d0')
                          : (isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0')}`,
                        background: isChecked
                          ? (isDark ? 'rgba(16,185,129,0.08)' : '#f0fdf4')
                          : 'var(--bg-raised)',
                        transition: 'all 0.15s ease',
                        opacity: isReadOnly && !isChecked ? 0.5 : 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isReadOnly}
                        onChange={() => {
                          if (isReadOnly) return;
                          setCheckedItems(prev => {
                            const next = new Set(prev);
                            next.has(item.id) ? next.delete(item.id) : next.add(item.id);
                            return next;
                          });
                        }}
                        className="w-4 h-4 flex-shrink-0 accent-emerald-500"
                        style={{ cursor: isReadOnly ? 'default' : 'pointer' }}
                      />
                      <SafeHtml as="span" html={item.label} style={{
                        fontSize: 13,
                        color: isChecked ? (isDark ? '#34d399' : '#065f46') : 'var(--text-secondary)',
                        fontWeight: isChecked ? 500 : 400,
                      }} />
                      {isChecked && (
                        <span style={{ marginLeft: 'auto', fontSize: 12, color: isDark ? '#34d399' : '#059669' }}>✓</span>
                      )}
                    </label>
                  );
                })}
              </div>
              {!editCandidate && (
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10 }}>
                  {checkedItems.size === effectiveChecklist.length
                    ? '✅ All items confirmed — you can save the candidate.'
                    : `⚠ ${effectiveChecklist.length - checkedItems.size} item${effectiveChecklist.length - checkedItems.size > 1 ? 's' : ''} remaining — all must be checked before saving.`
                  }
                </p>
              )}
            </div>
          )}
          {/* ─────────────────────────────────────────────────────────────── */}

          </>)}

          {/* Footer stays OUTSIDE the gate so Cancel is always reachable — on the
              resume step there is nothing to submit yet, so only Cancel shows. */}
          <div className="flex items-center justify-end gap-3 pt-2">
            {duplicateWarning && (
              <p className="text-xs font-semibold mr-auto" style={{ color: isDark ? '#f87171' : '#dc2626' }}>
                ⛔ Duplicate {editCandidate ? '— cannot update' : '— cannot add'}
              </p>
            )}
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            {(!!editCandidate || cvExtracted) && (
              <Button
                type="submit"
                loading={saving}
                disabled={!!duplicateWarning}
              >
                {editCandidate ? 'Update' : 'Add'}
              </Button>
            )}
          </div>
        </form>
      </Modal>

      {/* ── Per-entity History Panel ── */}
      {historyPanel.open && (
        <HistoryPanel
          entityId={historyPanel.id}
          entityName={historyPanel.name}
          onClose={() => setHistoryPanel({ open: false, id: null, name: '' })}
        />
      )}

      {/* ── Global History Feed ── */}
      {globalHistory && (
        <GlobalHistoryPanel
          module="Candidate"
          onClose={() => setGlobalHistory(false)}
        />
      )}

      {/* ── Resubmit Modal ── */}
      {resubmitCandidate && (() => {
        const c  = resubmitCandidate;
        const re = resubmitEditForm;
        const setRe = (key, val) => setResubmitEditForm(prev => ({ ...prev, [key]: val }));
        const alreadySubmittedIds = new Set(
          (c.submissions || []).map(s => s.requirementId).filter(Boolean)
        );
        const availableReqs = allRequirements.filter(
          r => r.status !== 'Closed' && !alreadySubmittedIds.has(r.id)
        );
        const STATUS_COLORS = {
          Applied:     { bg: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff', color: isDark ? '#a5b4fc' : '#4f46e5' },
          Shortlisted: { bg: isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb', color: isDark ? '#fcd34d' : '#b45309' },
          Interview:   { bg: isDark ? 'rgba(59,130,246,0.15)' : '#eff6ff', color: isDark ? '#93c5fd' : '#1d4ed8' },
          Hired:       { bg: isDark ? 'rgba(16,185,129,0.15)' : '#f0fdf4', color: isDark ? '#6ee7b7' : '#065f46' },
          Rejected:    { bg: isDark ? 'rgba(239,68,68,0.15)'  : '#fff5f5', color: isDark ? '#fca5a5' : '#b91c1c' },
        };
        const inp = { width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 13, background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#94a3b8'}`, color: isDark ? '#f1f5f9' : '#111827', outline: 'none', boxSizing: 'border-box' };
        const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: isDark ? '#94a3b8' : '#4b5563', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' };
        const card = { background: isDark ? '#1e2533' : '#ffffff', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.09)' : '#e2e8f0'}`, borderRadius: 12, boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' };
        const cardHead = (emoji, title) => (
          <div style={{ padding: '11px 16px', background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'}`, borderRadius: '11px 11px 0 0', display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ fontSize: 15 }}>{emoji}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#cbd5e1' : '#374151', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</span>
          </div>
        );
        return (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={() => setResubmitCandidate(null)}
          >
            <div
              style={{ background: isDark ? 'var(--bg-card)' : '#f4f6f9', borderRadius: 18, width: '100%', maxWidth: 780, maxHeight: '93vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.4)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e0e4ea'}`, overflow: 'hidden' }}
              onClick={e => e.stopPropagation()}
            >
              {/* ── Header ── */}
              <div style={{ padding: '18px 24px', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e0e4ea'}`, display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0, background: isDark ? 'rgba(255,255,255,0.02)' : '#ffffff', borderRadius: '18px 18px 0 0' }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,#0d9488,#0891b2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🔁</div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Resubmit Candidate</h2>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Update <strong style={{ color: 'var(--text-secondary)' }}>{c.fullName}</strong>'s details then pick a new requirement</p>
                </div>
                <Button variant="ghost" size="sm" iconOnly onClick={() => setResubmitCandidate(null)} style={{ flexShrink: 0 }}>✕</Button>
              </div>

              {/* ── Scrollable Body ── */}
              <div style={{ overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minHeight: 0 }}>

                {/* Basic Info card */}
                <div style={card}>
                  {cardHead('👤', 'Basic Information')}
                  <div className="grid grid-cols-1 sm:grid-cols-2" style={{ padding: '16px', gap: 14 }}>
                    <div><label style={lbl}>Full Name *</label><input style={inp} value={re.fullName || ''} onChange={e => setRe('fullName', e.target.value)} placeholder="Full name" /></div>
                    <div>
                      <label style={lbl}>Email</label>
                      <input style={{ ...inp, ...(re.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(re.email) ? { borderColor: '#ef4444' } : {}) }} type="email" value={re.email || ''} onChange={e => setRe('email', e.target.value)} placeholder="Email address" />
                      {re.email && !re.email.includes('@') && <p style={{ color: '#ef4444', fontSize: 11, marginTop: 3 }}>Email must include @</p>}
                      {re.email && re.email.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(re.email) && <p style={{ color: '#ef4444', fontSize: 11, marginTop: 3 }}>Enter a valid email (e.g. user@domain.com)</p>}
                    </div>
                    <div><label style={lbl}>Phone</label><input style={inp} value={re.phone || ''} onChange={e => setRe('phone', e.target.value)} placeholder="Phone number" /></div>
                    <div><label style={lbl}>Current Company</label><input style={inp} value={re.currentCompany || ''} onChange={e => setRe('currentCompany', e.target.value)} placeholder="Company name" /></div>
                    <div><label style={lbl}>Current Role</label><input style={inp} value={re.currentRole || ''} onChange={e => setRe('currentRole', e.target.value)} placeholder="Job title" /></div>
                    <div><label style={lbl}>Current Location</label><input style={inp} value={re.location || ''} onChange={e => setRe('location', e.target.value)} placeholder="City, Country" /></div>
                    <div><label style={lbl}>Preferred Location</label><input style={inp} value={re.preferredLocation || ''} onChange={e => setRe('preferredLocation', e.target.value)} placeholder="Preferred city" /></div>
                  </div>
                  {/* Total Experience full-width yrs/mos */}
                  <div style={{ padding: '0 16px 16px' }}>
                    <label style={lbl}>Total Experience</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#94a3b8'}`, borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9', padding: '0 10px' }}>
                        <input type="text" inputMode="numeric" placeholder="0" value={re.experienceYrs ?? ''}
                          onChange={e => setRe('experienceYrs', onlyDigits(e.target.value))}
                          style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, padding: '9px 0', minWidth: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>yrs</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#94a3b8'}`, borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9', padding: '0 10px' }}>
                        <input type="text" inputMode="numeric" placeholder="0" value={re.experienceMos ?? ''}
                          onChange={e => setRe('experienceMos', onlyDigits(e.target.value))}
                          style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, padding: '9px 0', minWidth: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>mos</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Compensation & Availability card */}
                <div style={card}>
                    {cardHead('💰', 'Compensation & Availability')}
                    <div className="grid grid-cols-1 sm:grid-cols-2" style={{ padding: '16px', gap: 14 }}>
                      <div>
                        <label style={lbl}>Current Salary (LPA)</label>
                        <div style={{ display: 'flex', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#94a3b8'}`, borderRadius: 8, overflow: 'hidden' }}>
                          <select value={re.currentSalaryCurrency || 'INR'} onChange={e => setRe('currentSalaryCurrency', e.target.value)}
                            style={{ background: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0', border: 'none', borderRight: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#94a3b8'}`, padding: '10px 10px', fontSize: 12, fontWeight: 700, color: isDark ? '#e2e8f0' : '#374151', cursor: 'pointer', outline: 'none', flexShrink: 0 }}>
                            {['USD','INR','EUR','GBP','AED','SGD','AUD','CAD','JPY','MYR','QAR','SAR'].map(cur => <option key={cur}>{cur}</option>)}
                          </select>
                          <input type="text" inputMode="decimal" value={re.currentSalary || ''} onChange={e => setRe('currentSalary', onlyDecimal(e.target.value))} placeholder="Amount"
                            style={{ flex: 1, padding: '10px 12px', fontSize: 13, background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9', border: 'none', color: isDark ? '#f1f5f9' : '#111827', outline: 'none', minWidth: 0 }} />
                        </div>
                      </div>
                      <div>
                        <label style={lbl}>Expected Salary (LPA)</label>
                        <div style={{ display: 'flex', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#94a3b8'}`, borderRadius: 8, overflow: 'hidden' }}>
                          <select value={re.expectedSalaryCurrency || 'INR'} onChange={e => setRe('expectedSalaryCurrency', e.target.value)}
                            style={{ background: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0', border: 'none', borderRight: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#94a3b8'}`, padding: '10px 10px', fontSize: 12, fontWeight: 700, color: isDark ? '#e2e8f0' : '#374151', cursor: 'pointer', outline: 'none', flexShrink: 0 }}>
                            {['USD','INR','EUR','GBP','AED','SGD','AUD','CAD','JPY','MYR','QAR','SAR'].map(cur => <option key={cur}>{cur}</option>)}
                          </select>
                          <input type="text" inputMode="decimal" value={re.expectedSalary || ''} onChange={e => setRe('expectedSalary', onlyDecimal(e.target.value))} placeholder="Amount"
                            style={{ flex: 1, padding: '10px 12px', fontSize: 13, background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9', border: 'none', color: isDark ? '#f1f5f9' : '#111827', outline: 'none', minWidth: 0 }} />
                        </div>
                      </div>
                      <div>
                        <label style={lbl}>Notice Period</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', background: isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0', borderRadius: 99, padding: 3, gap: 2, marginBottom: 10, maxWidth: '100%' }}>
                          <Button type="button" size="xs" variant={!re.servingNoticePeriod ? 'primary' : 'ghost'} onClick={() => setRe('servingNoticePeriod', false)} style={{ borderRadius: 99 }}>Fixed Days</Button>
                          <Button type="button" size="xs" variant={re.servingNoticePeriod ? 'warning' : 'ghost'} onClick={() => setRe('servingNoticePeriod', true)} style={{ borderRadius: 99 }}>🕐 Serving</Button>
                        </div>
                        <RichTextEditor label="Notice Period Notes" rows={3} value={re.noticePeriodNotes || ''}
                          onChange={(html) => setRe('noticePeriodNotes', html)}
                          placeholder="e.g. 30 days, negotiable to 15 with buyout, serving — LWD 22 Aug…" />
                      </div>
                    </div>
                </div>

                {/* Offer In Hand card */}
                <div style={card}>
                    {cardHead('🤝', 'Offer In Hand')}
                    <div style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 10, background: isDark ? 'rgba(255,255,255,0.04)' : '#f7f8fa', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e6eb'}` }}>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#e2e8f0' : '#111827', margin: 0 }}>Candidate has a competing offer?</p>
                          <p style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#6b7280', marginTop: 3, marginBottom: 0 }}>Enable to log company, package &amp; joining date</p>
                        </div>
                        <div onClick={() => setRe('offerInHand', !re.offerInHand)}
                          style={{ width: 48, height: 27, borderRadius: 99, background: re.offerInHand ? '#10b981' : (isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'), cursor: 'pointer', position: 'relative', transition: 'background 0.25s', flexShrink: 0, marginLeft: 16 }}>
                          <div style={{ position: 'absolute', top: 3, left: re.offerInHand ? 24 : 3, width: 21, height: 21, borderRadius: '50%', background: '#fff', transition: 'left 0.25s', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }} />
                        </div>
                      </div>
                      {re.offerInHand && (
                        <div className="grid grid-cols-1 sm:grid-cols-3" style={{ gap: 12, marginTop: 12 }}>
                          <div><label style={lbl}>Company</label><input style={inp} value={re.offerCompany || ''} onChange={e => setRe('offerCompany', e.target.value)} placeholder="Company name" /></div>
                          <div><label style={lbl}>Package</label><input style={inp} value={re.offerPackage || ''} onChange={e => setRe('offerPackage', e.target.value)} placeholder="e.g. 15 LPA" /></div>
                          <div><label style={lbl}>Date of Joining</label><input type="date" style={inp} value={re.offerDoj || ''} onChange={e => setRe('offerDoj', e.target.value)} /></div>
                        </div>
                      )}
                    </div>
                  </div>

                {/* Skills card */}
                <div style={card}>
                  {cardHead('🛠', 'Skills & Experience')}
                  <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div><label style={lbl}>Skills (comma-separated)</label><input style={inp} value={re.skills || ''} onChange={e => setRe('skills', e.target.value)} placeholder="e.g. React, Node.js, Python" /></div>
                    <div><RichTextEditor label="Relevant Experience Summary" rows={3} value={re.experienceSummary || ''}
                      onChange={(html) => setRe('experienceSummary', html)}
                      placeholder="Write-up of the candidate's relevant experience — domains, key projects, achievements…" /></div>
                    <div><RichTextEditor label="Notes" rows={3} value={re.notes || ''} onChange={(html) => setRe('notes', html)} placeholder="Internal recruiter notes…" /></div>
                  </div>
                </div>

                {/* ── Submit Zone ── */}
                <div style={{ background: isDark ? 'rgba(13,148,136,0.07)' : '#f0fdfa', border: `1px solid ${isDark ? 'rgba(13,148,136,0.2)' : '#99f6e4'}`, borderRadius: 12 }}>
                  <div style={{ padding: '11px 16px', background: isDark ? 'rgba(13,148,136,0.1)' : '#ccfbf1', borderBottom: `1px solid ${isDark ? 'rgba(13,148,136,0.15)' : '#99f6e4'}`, borderRadius: '11px 11px 0 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 15 }}>📋</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#5eead4' : '#0f766e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Submit to Requirement</span>
                  </div>
                  <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {c.submissions?.length > 0 && (
                      <div>
                        <p style={{ ...lbl, color: isDark ? '#5eead4' : '#0f766e' }}>Previously submitted to</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {c.submissions.map(s => {
                            const sc = STATUS_COLORS[s.status] || STATUS_COLORS.Applied;
                            return (
                              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 8px', borderRadius: 99, background: isDark ? 'rgba(255,255,255,0.06)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, fontSize: 11 }}>
                                <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{s.requirement?.jobTitle || '—'}</span>
                                {s.client?.clientName && <span style={{ color: 'var(--text-muted)' }}>· {s.client.clientName}</span>}
                                <span style={{ padding: '1px 7px', borderRadius: 99, fontWeight: 600, fontSize: 10, background: sc.bg, color: sc.color }}>{s.status}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div>
                      <label style={{ ...lbl, color: isDark ? '#5eead4' : '#0f766e' }}>Select new requirement *</label>
                      {availableReqs.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '16px', borderRadius: 10, background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`, color: 'var(--text-muted)', fontSize: 13 }}>
                          🎉 Already submitted to all available open requirements.
                        </div>
                      ) : (
                        <SearchableSelect
                          required
                          placeholder="— Pick a requirement —"
                          value={resubmitReqId}
                          onChange={val => setResubmitReqId(val)}
                          options={availableReqs.map(r => ({
                            value: r.id,
                            label: r.jobTitle,
                            sub:   `${r.client?.clientName || ''}${r.minExperience != null ? ` · ${r.minExperience}–${r.maxExperience || '?'} yrs` : ''} · ${r.status}`,
                          }))}
                        />
                      )}
                    </div>
                    <div>
                      <label style={{ ...lbl, color: isDark ? '#5eead4' : '#0f766e' }}>Internal Notes (optional)</label>
                      <RichTextEditor rows={3} value={resubmitNotes} onChange={(html) => setResubmitNotes(html)} placeholder="Any specific notes for this submission — added to the candidate's internal notes…" />
                    </div>

                    {/* Override Billing Rate */}
                    <div>
                      <label style={{ ...lbl, color: isDark ? '#5eead4' : '#0f766e' }}>Override Billing Rate (optional)</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <select
                          value={resubmitBillingCurrency}
                          onChange={e => setResubmitBillingCurrency(e.target.value)}
                          style={{ ...inp, minWidth: 90, width: 'auto', padding: '8px 10px' }}
                        >
                          {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="Amount"
                          value={resubmitBillingCost}
                          onChange={e => setResubmitBillingCost(onlyDecimal(e.target.value))}
                          style={{ ...inp, flex: '1 1 120px', minWidth: 0 }}
                        />
                        <select
                          value={resubmitBillingType}
                          onChange={e => setResubmitBillingType(e.target.value)}
                          style={{ ...inp, minWidth: 120, width: 'auto', padding: '8px 10px' }}
                        >
                          <option value="">Type</option>
                          {BILLING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      {resubmitBillingCost && (
                        <p style={{ fontSize: 11, color: isDark ? '#34d399' : '#059669', marginTop: 4 }}>
                          ⚡ {resubmitBillingCurrency} {Number(resubmitBillingCost).toLocaleString()}{resubmitBillingType ? ` / ${resubmitBillingType}` : ''}
                        </p>
                      )}
                    </div>

                    {/* Client Notes */}
                    <div>
                      <label style={{ ...lbl, color: isDark ? '#5eead4' : '#0f766e' }}>Client Notes (optional)</label>
                      <RichTextEditor
                        rows={3}
                        value={resubmitClientNotes}
                        onChange={(html) => setResubmitClientNotes(html)}
                        placeholder="Visible to client"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{ padding: '14px 20px', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e0e4ea'}`, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
                {!resubmitCandidate.resumeName && (
                  <span style={{ marginRight: 'auto', fontSize: 12, color: isDark ? '#f87171' : '#dc2626' }}>
                    ⚠ No resume on file — upload it via Edit before resubmitting.
                  </span>
                )}
                <Button variant="ghost" onClick={() => setResubmitCandidate(null)}>Cancel</Button>
                <Button variant="primary" onClick={handleResubmit} disabled={!resubmitReqId || resubmitting || !resubmitCandidate.resumeName}>
                  {resubmitting ? 'Submitting…' : 'Resubmit Candidate'}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Delete Confirm Modal ── */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={() => setConfirmDelete(null)} />
          <div style={{ position: 'relative', background: isDark ? '#1e2535' : '#ffffff', borderRadius: 16, padding: '28px', width: '100%', maxWidth: 400 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: isDark ? '#f1f5f9' : '#111827' }}>Delete Candidate?</h3>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: isDark ? '#94a3b8' : '#6b7280', lineHeight: 1.6 }}>This will permanently remove the candidate and all associated data.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #d1d5db', background: 'transparent', color: isDark ? '#f1f5f9' : '#374151', cursor: 'pointer', fontWeight: 500, fontSize: 14 }}>Cancel</button>
              <button onClick={handleDeleteConfirmed} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
